import { useEffect, useMemo, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ShieldCheck, Loader2, LogOut, Mail, Phone, Calendar, Sparkles, DollarSign, FileText, MessageSquareWarning, Radar, Trash2, FileDown, ArrowUpRight, Search, X, Paperclip, ChevronDown, ChevronUp, Eye, EyeOff, Star, Target, Plus, Bot, Lock, StickyNote, Save, Pencil, Pin, PinOff, ArrowRightLeft, Users, Wallet, CalendarDays, LayoutList, Video, Gauge, Award, Check, CreditCard, GripVertical, RotateCcw, Handshake, Lightbulb, Headphones, Maximize2, PanelLeft, FolderTree, BookOpen, Building2 } from "lucide-react";
import { api } from "../lib/api";
import { TIMEZONES, detectTimezone, wallTimeToIso } from "../lib/timezones";
import ElevareMark from "../components/site/ElevareMark";
import AdminReputationRepair from "../components/site/AdminReputationRepair";
import ProspectorTab from "../components/site/ProspectorTab";
import OutreachContacts from "../components/site/OutreachContacts";
import AdminPaymentsFolders from "../components/site/AdminPaymentsFolders";
import AnniversaryAlerts from "../components/site/AnniversaryAlerts";
import ClientProgress from "../components/site/ClientProgress";
import AiOps from "../components/site/AiOps";
import ClientVault from "../components/site/ClientVault";
import CaseStudiesAdmin from "../components/site/CaseStudiesAdmin";
import ReferralPartners from "../components/site/ReferralPartners";
import RecommendationsBoard from "../components/site/RecommendationsBoard";
import SupportBoard from "../components/site/SupportBoard";
import AdminWorkspace from "../components/site/AdminWorkspace";
import HandbookBoard from "../components/site/HandbookBoard";
import AdminTransfers from "../components/site/AdminTransfers";
import BusinessHealthBoard from "../components/site/BusinessHealthBoard";
import LeadProfileDrawer from "../components/site/LeadProfileDrawer";
import TeamPerformanceCard from "../components/site/TeamPerformanceCard";
import AdminAuthGate from "../components/site/AdminAuthGate";
import PendingApprovalsCard from "../components/site/PendingApprovalsCard";
import OnboardingReviewCard from "../components/site/OnboardingReviewCard";
import AssessmentsOverviewCard from "../components/site/AssessmentsOverviewCard";
import StaffOnboarding from "../components/site/StaffOnboarding";
import StaffAdmin from "../components/site/StaffAdmin";
import FoundersBank from "../components/site/FoundersBank";
import DownloadApprovals from "../components/site/DownloadApprovals";
import PayrollCard from "../components/site/PayrollCard";
import SubscriptionsCard from "../components/site/SubscriptionsCard";
import ProposePlanModal from "../components/site/ProposePlanModal";
import PlanSentChip, { usePlanSentMap } from "../components/site/PlanSentChip";
import ClientBbbForecast from "../components/site/ClientBbbForecast";
import GoogleCalendarConnect from "../components/site/GoogleCalendarConnect";
import AdminProfileMenu from "../components/site/AdminProfileMenu";
import AdminAccess from "../components/site/AdminAccess";
import OverviewDownloads from "../components/site/OverviewDownloads";
import { scoreMeta } from "../lib/healthScore";
import BookingsCalendar from "../components/site/BookingsCalendar";

const TABS = [
  { id: "leads", label: "Leads", icon: Mail },
  { id: "outreach-contacts", label: "Outreach Contacts", icon: Building2 },
  { id: "bookings", label: "Bookings", icon: Calendar },
  { id: "predictions", label: "Predictions", icon: Sparkles },
  { id: "payments", label: "Payments", icon: DollarSign, sensitive: true },
  { id: "expenses", label: "Expense AI", icon: FileText },
  { id: "progress", label: "Progress", icon: Target },
  { id: "vault", label: "CRM", icon: Lock },
  { id: "health", label: "Health Score", icon: Gauge },
  { id: "transfers", label: "Transfers", icon: ArrowRightLeft },
  { id: "ai-ops", label: "AI Ops", icon: Bot },
  { id: "reputation", label: "Reputation AI", icon: MessageSquareWarning },
  { id: "prospector", label: "Prospector", icon: Radar },
  { id: "case-studies", label: "Case Studies", icon: Award },
  { id: "referrals", label: "Referrals", icon: Handshake },
  { id: "recommendations", label: "Recommendations", icon: Lightbulb },
  { id: "support", label: "Support", icon: Headphones },
  { id: "workspace", label: "Workspace", icon: FolderTree },
  { id: "handbook", label: "Handbook", icon: BookOpen },
  // Sensitive tabs: hidden from members by default, but a founder can still grant
  // them per-member in the Access tab. (payments/money/admin surfaces)
  { id: "subscriptions", label: "Subscriptions", icon: CreditCard, sensitive: true },
  { id: "bank", label: "Bank", icon: Wallet, sensitive: true },
  { id: "downloads", label: "Downloads", icon: FileDown, sensitive: true },
  { id: "staff", label: "Staff", icon: Users, sensitive: true },
  // The permissions console itself — founder-only, never grantable to members.
  { id: "access", label: "Access", icon: ShieldCheck, founderLock: true },
];

// Tabs a founder can hand to a member (everything except the permissions console).
const GRANTABLE_TABS = TABS.filter((t) => !t.founderLock);

const fmtDate = (iso) => {
  try { return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }); }
  catch { return iso; }
};
const fmtSlot = (iso) => {
  try { return new Date(iso).toLocaleString(undefined, { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }); }
  catch { return iso; }
};

export default function Admin() {
  const [secret, setSecret] = useState(() => localStorage.getItem("elevare_admin_secret") || "");
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  // JWT-authenticated user record (populated after AdminAuthGate.onAuthed or resumed session).
  const [me, setMe] = useState(null);
  // "checking" | "needed" | "submitted" | "cleared". Only relevant for non-founder JWT sessions.
  const [onboardingGate, setOnboardingGate] = useState("cleared");
  // Shrink the sticky header once the user scrolls past the fold.
  const [headerCompact, setHeaderCompact] = useState(false);
  useEffect(() => {
    const onScroll = () => setHeaderCompact(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  const [overview, setOverview] = useState(null);
  const [tab, setTab] = useState("leads");
  const [prospectorSeed, setProspectorSeed] = useState(null);
  const launchAssessment = (client) => {
    const loc = [client?.address?.city, client?.address?.state].filter(Boolean).join(", ");
    setProspectorSeed({ name: client?.business_name || client?.primary_contact?.name || "", location: loc, ts: Date.now() });
    setTab("prospector");
  };
  const [tabOrder, setTabOrder] = useState([]);
  const [hiddenTabs, setHiddenTabs] = useState([]);
  const [defaultTab, setDefaultTab] = useState(null);
  const [editLayout, setEditLayout] = useState(false);
  const [dragId, setDragId] = useState(null);
  const dragOrderRef = useRef(null);
  const [focusMode, setFocusMode] = useState(false);
  const focusKey = () => uKey("elevare_focus");
  const toggleFocus = () => setFocusMode((v) => {
    const nv = !v;
    try { nv ? localStorage.setItem(focusKey(), "1") : localStorage.removeItem(focusKey()); } catch { /* ignore */ }
    return nv;
  });
  const appliedDefault = useRef(false);
  const appliedDeepLink = useRef(false);
  const cloudSync = !!(me && me.id);
  const uKey = (base) => (localStorage.getItem("elevare_admin_user") ? `${base}_${localStorage.getItem("elevare_admin_user")}` : base);
  const orderKey = () => uKey("elevare_tab_order");
  const hiddenKey = () => uKey("elevare_hidden_tabs");
  const defaultKey = () => uKey("elevare_default_tab");
  // Load layout prefs: cloud (per-account) for JWT users, else localStorage.
  useEffect(() => {
    if (cloudSync && me.layout_prefs) {
      const p = me.layout_prefs || {};
      setTabOrder(Array.isArray(p.tab_order) ? p.tab_order : []);
      setHiddenTabs(Array.isArray(p.hidden_tabs) ? p.hidden_tabs : []);
      setDefaultTab(p.default_tab || null);
      return;
    }
    try { const s = JSON.parse(localStorage.getItem(orderKey()) || "[]"); if (Array.isArray(s)) setTabOrder(s); } catch { /* ignore */ }
    try { const h = JSON.parse(localStorage.getItem(hiddenKey()) || "[]"); if (Array.isArray(h)) setHiddenTabs(h); } catch { /* ignore */ }
    setDefaultTab(localStorage.getItem(defaultKey()) || null);
  }, [me?.username]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { try { setFocusMode(localStorage.getItem(focusKey()) === "1"); } catch { /* ignore */ } }, [me?.username]); // eslint-disable-line react-hooks/exhaustive-deps
  // Persist a layout patch to the backend (JWT) plus a localStorage cache.
  const saveLayout = (patch) => {
    if ("tab_order" in patch) { try { localStorage.setItem(orderKey(), JSON.stringify(patch.tab_order)); } catch { /* ignore */ } }
    if ("hidden_tabs" in patch) { try { localStorage.setItem(hiddenKey(), JSON.stringify(patch.hidden_tabs)); } catch { /* ignore */ } }
    if ("default_tab" in patch) { try { patch.default_tab ? localStorage.setItem(defaultKey(), patch.default_tab) : localStorage.removeItem(defaultKey()); } catch { /* ignore */ } }
    if (cloudSync) api.patch("/admin-auth/me/layout", patch).catch(() => {});
  };
  const persistTabOrder = (ids) => { setTabOrder(ids); saveLayout({ tab_order: ids }); };
  const toggleHiddenTab = (id) => setHiddenTabs((prev) => { const next = prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]; saveLayout({ hidden_tabs: next }); return next; });
  const setAsDefault = (id) => { const next = defaultTab === id ? null : id; setDefaultTab(next); saveLayout({ default_tab: next }); };
  const resetTabOrder = () => { setTabOrder([]); setHiddenTabs([]); setDefaultTab(null); saveLayout({ tab_order: [], hidden_tabs: [], default_tab: null }); };
  const [selectedLeads, setSelectedLeads] = useState(() => new Set());
  // Bulk-select sets for the tabs that support multi-remove.
  const [selectedPredictions, setSelectedPredictions] = useState(() => new Set());
  const [selectedPayments, setSelectedPayments] = useState(() => new Set());
  const [selectedExpenses, setSelectedExpenses] = useState(() => new Set());
  // Confirmation modal for bulk delete (payments has an extra "refund" toggle).
  const [bulkConfirm, setBulkConfirm] = useState(null); // { kind, ids, refund? }
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [openLead, setOpenLead] = useState(null);
  const [leadSort, setLeadSort] = useState("recent"); // "recent" | "health"
  const [proposeFor, setProposeFor] = useState(null); // { name, email, business_name }
  const [planSent, refreshPlanSent] = usePlanSentMap(me?.role === "founder");
  const [showAddLead, setShowAddLead] = useState(false);
  const [savingLead, setSavingLead] = useState(false);
  const [leadDraft, setLeadDraft] = useState({ name: "", email: "", company: "", phone: "", source: "", notes: "" });
  // Snapshot KPI manual overrides (founder can click a number and set it).
  const [snapOverrides, setSnapOverrides] = useState({});
  const [snapEditKey, setSnapEditKey] = useState(null);
  const [snapDraft, setSnapDraft] = useState("");

  const saveSnapOverride = async (key, value) => {
    const next = { ...snapOverrides };
    if (value === "" || value === null || value === undefined) delete next[key];
    else next[key] = Number(value);
    setSnapOverrides(next);
    setSnapEditKey(null);
    try {
      await api.put("/admin/site-settings", { snapshot_overrides: next });
      toast.success("Snapshot updated.");
    } catch { toast.error("Could not save the number."); }
  };

  const load = async (s) => {
    setLoading(true);
    try {
      // JWT-authenticated sessions carry a cookie already; don't leak the
      // sentinel "jwt:..." into the query string.
      const url = s.startsWith("jwt:")
        ? `/admin/overview`
        : `/admin/overview?secret=${encodeURIComponent(s)}`;
      const { data } = await api.get(url);
      setOverview(data);
      // Backfill `me` for tab-filtering (founder-only rails) whenever the
      // overview response carries the current-user snapshot.
      if (data?.current_user && !me) setMe(data.current_user);
      try {
        const { data: settings } = await api.get("/admin/site-settings");
        setSnapOverrides(settings?.snapshot_overrides || {});
      } catch { /* non-critical */ }
    } catch (err) {
      if (err?.response?.status === 401) {
        setSecret("");
        localStorage.removeItem("elevare_admin_secret");
        toast.error("Sign-in expired.");
      } else {
        toast.error("Could not load admin data.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (secret) load(secret); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async (e) => {
    e.preventDefault();
    if (!input.trim()) return;
    setLoading(true);
    try {
      await api.post("/admin/login", { secret: input.trim() });
      localStorage.setItem("elevare_admin_secret", input.trim());
      setSecret(input.trim());
      await load(input.trim());
      toast.success("Signed in.");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Login failed.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try { await api.post("/admin-auth/logout"); } catch (_) { /* no-op if not signed via JWT */ }
    localStorage.removeItem("elevare_admin_secret");
    localStorage.removeItem("elevare_admin_user");
    setSecret("");
    setMe(null);
    setOnboardingGate("cleared");
    setOverview(null);
  };

  // After JWT login (or resumed session), check whether this user still owes
  // us onboarding data. Founders are always cleared.
  const checkOnboarding = async (user) => {
    setMe(user);
    if (user?.role === "founder") { setOnboardingGate("cleared"); return true; }
    setOnboardingGate("checking");
    try {
      const { data } = await api.get("/admin-auth/onboarding");
      if (data?.needs_onboarding) {
        setOnboardingGate((data?.status || "incomplete") === "submitted" ? "submitted" : "needed");
        return false;
      }
      setOnboardingGate("cleared");
      return true;
    } catch (_) {
      setOnboardingGate("needed");
      return false;
    }
  };

  const deleteLead = async (lead) => {
    if (!window.confirm(`Delete lead from ${lead.name || lead.email}? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/leads/${lead.id}?secret=${encodeURIComponent(secret)}`);
      setOverview((o) => o ? { ...o, leads: o.leads.filter((x) => x.id !== lead.id) } : o);
      setSelectedLeads((s) => { const n = new Set(s); n.delete(lead.id); return n; });
      toast.success("Lead removed.");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Could not delete lead.";
      toast.error(msg);
    }
  };

  const deletePrediction = async (p) => {
    const name = p?.input?.company || p?.business_name || "this forecast";
    if (!window.confirm(`Remove the AI forecast for ${name}? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/predictions/${p.id}`);
      setOverview((o) => o ? { ...o, predictions: (o.predictions || []).filter((x) => x.id !== p.id) } : o);
      toast.success("Forecast removed.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not remove forecast.");
    }
  };

  // Custom modal instead of window.confirm — we need the "also refund via Stripe" toggle.
  const [paymentDeleteTarget, setPaymentDeleteTarget] = useState(null); // { p, refund }
  const [paymentDeleting, setPaymentDeleting] = useState(false);

  const deletePayment = (p) => {
    // Open the dialog. Default refund=true for paid rows since founders
    // usually want to remove + refund together.
    const isPaid = (p.payment_status || p.status) === "paid";
    setPaymentDeleteTarget({ p, refund: isPaid });
  };

  const confirmDeletePayment = async () => {
    if (!paymentDeleteTarget) return;
    const { p, refund } = paymentDeleteTarget;
    const key = p.session_id || p.id;
    setPaymentDeleting(true);
    try {
      const { data } = await api.delete(
        `/admin/payments/${encodeURIComponent(key)}${refund ? "?refund=true" : ""}`,
      );
      setOverview((o) => o ? { ...o, payments: (o.payments || []).filter((x) => (x.session_id || x.id) !== key) } : o);
      toast.success(data?.refund_id ? "Refund issued & row removed." : "Payment removed.");
      setPaymentDeleteTarget(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not remove payment.");
    } finally {
      setPaymentDeleting(false);
    }
  };

  // Delete an entire client's payment folder (all their rows). Ledger-only by
  // default — no Stripe refunds (founder chose this as the safe default).
  const deleteFolder = (group) => {
    const ids = (group?.rows || []).map((p) => p.session_id || p.id).filter(Boolean);
    if (!ids.length) return;
    setBulkConfirm({ kind: "payments", ids, refund: false });
  };

  const deleteExpenseAnalysis = async (a) => {
    const name = a.business_name || a.client_id || "this analysis";
    if (!window.confirm(`Remove the Expense AI analysis for ${name}? This deletes the worksheet permanently.`)) return;
    try {
      await api.delete(`/admin/expenses/${a.id}`);
      setExpenses((list) => (list || []).filter((x) => x.id !== a.id));
      toast.success("Analysis removed.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not remove analysis.");
    }
  };

  // ---- Bulk delete helpers ----
  // Every "select" set uses the corresponding row identifier (id / session_id / client_id).
  const toggleSet = (setter) => (id) => setter((s) => {
    const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n;
  });
  const togglePrediction = toggleSet(setSelectedPredictions);
  const togglePayment = toggleSet(setSelectedPayments);
  const toggleExpense = toggleSet(setSelectedExpenses);

  const toggleAllInSet = (setter, allIds) => () => setter((s) => {
    const allSelected = allIds.length > 0 && allIds.every((id) => s.has(id));
    return allSelected ? new Set() : new Set(allIds);
  });

  const confirmBulkDelete = async () => {
    if (!bulkConfirm || !bulkConfirm.ids?.length) return;
    const { kind, ids, refund } = bulkConfirm;
    setBulkDeleting(true);
    try {
      if (kind === "predictions") {
        const { data } = await api.post("/admin/predictions/bulk-delete", { ids });
        setOverview((o) => o ? { ...o, predictions: (o.predictions || []).filter((x) => !ids.includes(x.id)) } : o);
        setSelectedPredictions(new Set());
        toast.success(`Removed ${data.deleted || ids.length} forecast${(data.deleted || ids.length) === 1 ? "" : "s"}.`);
      } else if (kind === "payments") {
        const { data } = await api.post(`/admin/payments/bulk-delete${refund ? "?refund=true" : ""}`, { ids });
        setOverview((o) => o ? { ...o, payments: (o.payments || []).filter((x) => !ids.includes(x.session_id || x.id)) } : o);
        setSelectedPayments(new Set());
        const refundedCount = data.refunds?.length || 0;
        const errCount = data.errors?.length || 0;
        const parts = [`Removed ${data.deleted || 0} row${(data.deleted || 0) === 1 ? "" : "s"}`];
        if (refundedCount) parts.push(`refunded ${refundedCount}`);
        if (errCount) parts.push(`${errCount} refund error${errCount === 1 ? "" : "s"}`);
        (errCount ? toast.warning : toast.success)(parts.join(" · "));
      } else if (kind === "expenses") {
        const { data } = await api.post("/admin/expenses/bulk-delete", { ids });
        setExpenses((list) => (list || []).filter((x) => !ids.includes(x.id)));
        setSelectedExpenses(new Set());
        toast.success(`Removed ${data.deleted || ids.length} analys${(data.deleted || ids.length) === 1 ? "is" : "es"}.`);
      }
      setBulkConfirm(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Bulk remove failed.");
    } finally {
      setBulkDeleting(false);
    }
  };

  const toggleLead = (id) => {
    setSelectedLeads((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };

  const toggleAllLeads = () => {
    if (!overview?.leads?.length) return;
    setSelectedLeads((s) => {
      if (s.size === overview.leads.length) return new Set();
      return new Set(overview.leads.map((l) => l.id).filter(Boolean));
    });
  };

  const bulkDeleteLeads = async () => {
    const ids = Array.from(selectedLeads);
    if (!ids.length) return;
    if (!window.confirm(`Delete ${ids.length} lead${ids.length === 1 ? "" : "s"}? This cannot be undone.`)) return;
    try {
      const { data } = await api.post("/admin/leads/bulk-delete", { ids, secret });
      setOverview((o) => o ? { ...o, leads: o.leads.filter((l) => !selectedLeads.has(l.id)) } : o);
      setSelectedLeads(new Set());
      toast.success(`${data.deleted} lead${data.deleted === 1 ? "" : "s"} removed.`);
    } catch {
      toast.error("Bulk delete failed.");
    }
  };

  const saveNewLead = async () => {
    const name = (leadDraft.name || "").trim();
    const email = (leadDraft.email || "").trim().toLowerCase();
    if (!name) return toast.error("Name is required.");
    if (!email || !/^\S+@\S+\.\S+$/.test(email)) return toast.error("Enter a valid email.");
    setSavingLead(true);
    try {
      const addedBy = (() => {
        try { return localStorage.getItem("elevare_transfer_approver") || "admin"; }
        catch { return "admin"; }
      })();
      const { data: created } = await api.post("/admin/leads", {
        name,
        email,
        company: leadDraft.company.trim() || null,
        phone: leadDraft.phone.trim() || null,
        source: leadDraft.source.trim() || null,
        notes: leadDraft.notes.trim() || null,
        added_by: addedBy,
      });
      setOverview((o) => o ? { ...o, leads: [created, ...o.leads] } : o);
      toast.success(`${created.name} added.`);
      setShowAddLead(false);
      setLeadDraft({ name: "", email: "", company: "", phone: "", source: "", notes: "" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add lead.");
    } finally {
      setSavingLead(false);
    }
  };

  const exportLeadsCsv = () => {
    if (!overview?.leads?.length) return;
    const src = selectedLeads.size
      ? overview.leads.filter((l) => selectedLeads.has(l.id))
      : overview.leads;
    const esc = (v) => {
      const s = (v ?? "").toString();
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const cols = ["created_at", "name", "email", "company", "message"];
    const rows = [cols.join(",")].concat(src.map((l) => cols.map((c) => esc(l[c])).join(",")));
    const blob = new Blob([rows.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `Elevare_leads_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
    toast.success(`${src.length} lead${src.length === 1 ? "" : "s"} exported.`);
  };

  const deleteBooking = async (bk) => {
    if (!bk?.id) { toast.error("Booking is missing an id."); return; }
    if (!window.confirm(`Delete booking with ${bk.name || bk.email}? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/bookings/${bk.id}?secret=${encodeURIComponent(secret)}`);
      setOverview((o) => o ? { ...o, bookings: o.bookings.filter((x) => x.id !== bk.id) } : o);
      toast.success("Booking removed.");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Could not delete booking.";
      toast.error(msg);
    }
  };

  const [showNewBooking, setShowNewBooking] = useState(false);
  const [creatingBooking, setCreatingBooking] = useState(false);
  const [bookingFilter, setBookingFilter] = useState("all"); // all | today | upcoming | past
  const [advisorFilter, setAdvisorFilter] = useState("all"); // all | diagnostic | chris
  const [bookingView, setBookingView] = useState("list"); // list | calendar
  const [calendarTz, setCalendarTz] = useState("America/New_York");
  const [newBooking, setNewBooking] = useState({
    date: "", time: "", tz: detectTimezone(), name: "", email: "", company: "", goal: "",
  });

  const setBookingAttendance = async (bk, value) => {
    if (!bk?.id) return;
    // Clicking the already-checked box clears the attendance mark.
    const next = bk.attendance === value ? null : value;
    try {
      const { data } = await api.patch(`/admin/bookings/${bk.id}/attendance`, { attendance: next });
      setOverview((o) => o ? { ...o, bookings: o.bookings.map((x) => x.id === bk.id ? { ...x, attendance: next } : x) } : o);
      if (next === "no_show") {
        toast.success(data?.follow_up_email ? "Marked as no-show — rebooking email sent to the client." : "Marked as no-show.");
      } else {
        toast.success(next === "showed" ? "Marked as showed." : "Attendance cleared.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update attendance.");
    }
  };

  const setBookingTz = async (bk, newTz) => {
    if (!bk?.id || !newTz || newTz === bk.tz) return;
    try {
      await api.patch(`/admin/bookings/${bk.id}/timezone`, { tz: newTz });
      setOverview((o) => o ? { ...o, bookings: o.bookings.map((x) => x.id === bk.id ? { ...x, tz: newTz } : x) } : o);
      toast.success("Time zone updated for this meeting.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update time zone.");
    }
  };

  const ensureBookingMeet = async (bk) => {
    if (!bk?.id) return;
    try {
      const { data } = await api.post(`/admin/bookings/${bk.id}/meet`);
      setOverview((o) => o ? { ...o, bookings: o.bookings.map((x) => x.id === bk.id ? { ...x, meet_url: data.meet_url, gcal_event_id: data.gcal_event_id } : x) } : o);
      toast.success("Google Meet link ready.");
      return data;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not generate a Meet link. Is Google Calendar connected?");
    }
  };

  const addBookingParticipant = async (bk, email) => {
    if (!bk?.id || !email) return;
    try {
      const { data } = await api.post(`/admin/bookings/${bk.id}/participants`, { emails: [email] });
      setOverview((o) => o ? { ...o, bookings: o.bookings.map((x) => x.id === bk.id ? { ...x, participants: data.participants, ...(data.meet_url ? { meet_url: data.meet_url } : {}) } : x) } : o);
      toast.success(data.invited ? "Participant added — calendar invite sent." : "Participant added.");
      return data;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not add participant.");
      throw err;
    }
  };

  const removeBookingParticipant = async (bk, email) => {
    if (!bk?.id || !email) return;
    try {
      const { data } = await api.delete(`/admin/bookings/${bk.id}/participants?email=${encodeURIComponent(email)}`);
      setOverview((o) => o ? { ...o, bookings: o.bookings.map((x) => x.id === bk.id ? { ...x, participants: data.participants } : x) } : o);
      toast.success("Participant removed.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not remove participant.");
    }
  };

  const createBooking = async (e) => {
    e.preventDefault();
    if (!newBooking.date || !newBooking.time || !newBooking.name || !newBooking.email) {
      toast.error("Date, time, name and email are required.");
      return;
    }
    const slot_iso = wallTimeToIso(newBooking.date, newBooking.time, newBooking.tz);
    if (!slot_iso) { toast.error("Invalid date or time."); return; }
    setCreatingBooking(true);
    try {
      const { data } = await api.post("/admin/bookings", {
        slot_iso,
        name: newBooking.name,
        email: newBooking.email,
        company: newBooking.company || null,
        goal: newBooking.goal || null,
        tz: newBooking.tz,
        secret,
      });
      setOverview((o) => o ? { ...o, bookings: [data, ...o.bookings] } : o);
      setShowNewBooking(false);
      setNewBooking({ date: "", time: "", tz: detectTimezone(), name: "", email: "", company: "", goal: "" });
      toast.success("Booking created — client emailed.");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Could not create booking.";
      toast.error(msg);
    } finally {
      setCreatingBooking(false);
    }
  };

  const [expenses, setExpenses] = useState(null);
  const [expensesLoading, setExpensesLoading] = useState(false);
  const [downloadingExpenseId, setDownloadingExpenseId] = useState(null);
  const [expenseQuery, setExpenseQuery] = useState("");
  const [predictionQuery, setPredictionQuery] = useState("");
  const [expandedVault, setExpandedVault] = useState(null); // client_id currently expanded
  const [vaultData, setVaultData] = useState({}); // client_id -> { loading, files }
  const [vaultQueries, setVaultQueries] = useState({}); // client_id -> search string
  const [notesData, setNotesData] = useState({}); // client_id -> { loading, notes }
  const [noteDrafts, setNoteDrafts] = useState({}); // client_id -> new-note text
  const [savingNoteCid, setSavingNoteCid] = useState(null);
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [editingNoteText, setEditingNoteText] = useState("");

  const loadExpenses = async () => {
    if (!secret) return;
    setExpensesLoading(true);
    try {
      const { data } = await api.get(`/admin/expenses?secret=${encodeURIComponent(secret)}`);
      setExpenses(data.analyses || []);
    } catch {
      toast.error("Could not load expense analyses.");
    } finally {
      setExpensesLoading(false);
    }
  };

  useEffect(() => {
    if (tab === "expenses" && secret && expenses === null && !expensesLoading) {
      loadExpenses();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, secret]);

  const downloadExpensePDF = async (a) => {
    setDownloadingExpenseId(a.id);
    try {
      const res = await api.get(
        `/admin/expenses/${a.id}/pdf?secret=${encodeURIComponent(secret)}`,
        { responseType: "blob" }
      );
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      const safe = (a.business_name || "worksheet").replace(/[^A-Za-z0-9_-]+/g, "_");
      link.download = `Elevare_Expenses_${safe}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Worksheet downloaded.");
    } catch {
      toast.error("Could not build worksheet.");
    } finally {
      setDownloadingExpenseId(null);
    }
  };

  const toggleVault = async (clientId) => {
    if (!clientId) { toast.error("No client ID on this analysis."); return; }
    if (expandedVault === clientId) { setExpandedVault(null); return; }
    setExpandedVault(clientId);
    if (!vaultData[clientId]) {
      setVaultData((v) => ({ ...v, [clientId]: { loading: true, files: [] } }));
      try {
        const { data } = await api.get(`/expenses/files?client_id=${encodeURIComponent(clientId)}`);
        setVaultData((v) => ({ ...v, [clientId]: { loading: false, files: data.files || [] } }));
      } catch {
        setVaultData((v) => ({ ...v, [clientId]: { loading: false, files: [] } }));
        toast.error("Could not load vault.");
      }
    }
    if (!notesData[clientId]) {
      setNotesData((n) => ({ ...n, [clientId]: { loading: true, notes: [] } }));
      try {
        const { data } = await api.get(`/expenses/notes?client_id=${encodeURIComponent(clientId)}`);
        setNotesData((n) => ({ ...n, [clientId]: { loading: false, notes: data.notes || [] } }));
      } catch {
        setNotesData((n) => ({ ...n, [clientId]: { loading: false, notes: [] } }));
      }
    }
  };

  const addClientNote = async (clientId, businessName) => {
    const text = (noteDrafts[clientId] || "").trim();
    if (!text) { toast.error("Note is empty."); return; }
    setSavingNoteCid(clientId);
    try {
      const { data } = await api.post("/expenses/notes", {
        client_id: clientId, text, business_name: businessName || null,
      });
      setNotesData((n) => {
        const b = n[clientId] || { loading: false, notes: [] };
        return { ...n, [clientId]: { ...b, notes: [data, ...(b.notes || [])] } };
      });
      setNoteDrafts((d) => ({ ...d, [clientId]: "" }));
      toast.success("Note saved.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save note.");
    } finally {
      setSavingNoteCid(null);
    }
  };

  const startEditNote = (note) => {
    setEditingNoteId(note.id);
    setEditingNoteText(note.text || "");
  };

  const saveEditNote = async (clientId, noteId) => {
    const text = editingNoteText.trim();
    if (!text) { toast.error("Note is empty."); return; }
    try {
      const { data } = await api.patch(`/expenses/notes/${noteId}`, {
        client_id: clientId, text,
      });
      setNotesData((n) => {
        const b = n[clientId];
        if (!b) return n;
        return { ...n, [clientId]: { ...b, notes: b.notes.map((x) => x.id === noteId ? data : x) } };
      });
      setEditingNoteId(null);
      setEditingNoteText("");
      toast.success("Note updated.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update note.");
    }
  };

  const deleteClientNote = async (clientId, noteId) => {
    if (!window.confirm("Delete this note?")) return;
    try {
      await api.delete(`/expenses/notes/${noteId}`);
      setNotesData((n) => {
        const b = n[clientId];
        if (!b) return n;
        return { ...n, [clientId]: { ...b, notes: b.notes.filter((x) => x.id !== noteId) } };
      });
      toast.success("Note removed.");
    } catch {
      toast.error("Could not remove note.");
    }
  };

  const togglePinNote = async (clientId, noteId) => {
    try {
      const { data } = await api.post(`/expenses/notes/${noteId}/pin`);
      setNotesData((n) => {
        const b = n[clientId];
        if (!b) return n;
        // Merge + re-sort (pinned first, then newest first).
        const merged = b.notes.map((x) => x.id === noteId ? data : x);
        merged.sort((a, b) => {
          const pa = a.pinned ? 0 : 1, pb = b.pinned ? 0 : 1;
          if (pa !== pb) return pa - pb;
          return (b.created_at || "").localeCompare(a.created_at || "");
        });
        return { ...n, [clientId]: { ...b, notes: merged } };
      });
      toast.success(data.pinned ? "Pinned to top." : "Unpinned.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not toggle pin.");
    }
  };

  const isViewable = (f) => {
    const m = (f?.mime || "").toLowerCase();
    const n = (f?.filename || "").toLowerCase();
    return m === "application/pdf" || m.startsWith("image/") ||
           n.endsWith(".pdf") || /\.(png|jpe?g|gif|webp|svg|bmp|heic|heif)$/i.test(n);
  };

  const previewVaultFile = async (f) => {
    try {
      const res = await api.get(`/expenses/files/${f.id}/download`, { responseType: "blob" });
      // Force PDF/image MIME so the browser opens inline instead of downloading.
      let type = f.mime || "application/octet-stream";
      const n = (f.filename || "").toLowerCase();
      if (n.endsWith(".pdf")) type = "application/pdf";
      else if (/\.(png)$/.test(n)) type = "image/png";
      else if (/\.(jpe?g)$/.test(n)) type = "image/jpeg";
      else if (/\.(gif)$/.test(n)) type = "image/gif";
      else if (/\.(webp)$/.test(n)) type = "image/webp";
      const url = window.URL.createObjectURL(new Blob([res.data], { type }));
      const w = window.open(url, "_blank", "noopener,noreferrer");
      if (!w) toast.error("Popup blocked — allow popups to preview.");
      // Give the new tab time to load the blob before revoking.
      setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Preview failed.";
      toast.error(msg);
    }
  };

  const downloadVaultFile = async (f) => {
    try {
      const res = await api.get(`/expenses/files/${f.id}/download`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: f.mime || "application/octet-stream" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = f.filename || "file";
      link.rel = "noopener";
      document.body.appendChild(link);
      link.click();
      // Defer revoke so the browser has time to start the download.
      setTimeout(() => {
        link.remove();
        window.URL.revokeObjectURL(url);
      }, 800);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Download failed.";
      toast.error(msg);
    }
  };

  const deleteVaultFile = async (f) => {
    if (!window.confirm(`Delete ${f.filename}?`)) return;
    try {
      await api.delete(`/expenses/files/${f.id}`);
      setVaultData((v) => {
        const bucket = v[f.client_id];
        if (!bucket) return v;
        return { ...v, [f.client_id]: { ...bucket, files: bucket.files.filter((x) => x.id !== f.id) } };
      });
      toast.success("File removed.");
    } catch {
      toast.error("Could not remove.");
    }
  };

  const [uploadingCid, setUploadingCid] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(null); // { cid, pct, loaded, total, name }
  const uploadVaultFile = async (clientId, businessName, file) => {
    if (!file || !clientId) return;
    if (file.size > 500 * 1024 * 1024) {
      toast.error("File too large — 500 MB max per file.");
      return;
    }
    setUploadingCid(clientId);
    setUploadProgress({ cid: clientId, pct: 0, loaded: 0, total: file.size, name: file.name });
    const form = new FormData();
    form.append("file", file);
    form.append("client_id", clientId);
    if (businessName) form.append("business_name", businessName);
    try {
      const { data } = await api.post("/expenses/files/upload", form, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 15 * 60 * 1000, // 15 min for large files
        onUploadProgress: (evt) => {
          const total = evt.total || file.size || 0;
          const loaded = evt.loaded || 0;
          const pct = total ? Math.min(100, Math.round((loaded / total) * 100)) : 0;
          setUploadProgress({ cid: clientId, pct, loaded, total, name: file.name });
        },
      });
      setVaultData((v) => {
        const bucket = v[clientId] || { loading: false, files: [] };
        return { ...v, [clientId]: { ...bucket, files: [data, ...(bucket.files || [])] } };
      });
      toast.success(`${file.name} uploaded.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed.");
    } finally {
      setUploadingCid(null);
      setTimeout(() => setUploadProgress(null), 800);
    }
  };

  const fmtBytes = (n) => {
    if (!n && n !== 0) return "";
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
    if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
    return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
  };

  const [supportUnread, setSupportUnread] = useState(0);
  const prevSupportUnread = useRef(0);
  useEffect(() => {
    let stop = false;
    const beep = () => {
      try {
        const Ctx = window.AudioContext || window.webkitAudioContext;
        if (!Ctx) return;
        const ctx = new Ctx(); const o = ctx.createOscillator(); const g = ctx.createGain();
        o.connect(g); g.connect(ctx.destination); o.type = "sine"; o.frequency.value = 880;
        g.gain.setValueAtTime(0.0001, ctx.currentTime); g.gain.exponentialRampToValueAtTime(0.15, ctx.currentTime + 0.02);
        g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.35);
        o.start(); o.stop(ctx.currentTime + 0.36);
      } catch { /* ignore */ }
    };
    const poll = async () => {
      try {
        const { data } = await api.get("/admin/support");
        const total = (data.sessions || []).reduce((n, s) => n + (s.admin_unread || 0), 0);
        if (!stop) {
          if (total > prevSupportUnread.current) { beep(); toast.message("New client message in Support", { description: `${total} unread` }); }
          prevSupportUnread.current = total;
          setSupportUnread(total);
        }
      } catch { /* not authed yet */ }
    };
    poll();
    const iv = setInterval(poll, 15000);
    return () => { stop = true; clearInterval(iv); };
  }, []);

  const counts = useMemo(() => ({
    leads: overview?.leads?.length || 0,
    predictions: overview?.predictions?.length || 0,
    payments: overview?.payments?.length || 0,
    expenses: expenses?.length ?? overview?.expense_count ?? 0,
    "case-studies": overview?.case_count ?? 0,
    support: supportUnread || undefined,
  }), [overview, expenses, supportUnread]);

  const filteredExpenses = useMemo(() => {
    if (!expenses) return [];
    const q = expenseQuery.trim().toLowerCase();
    if (!q) return expenses;
    return expenses.filter((a) => {
      const biz = (a.business_name || "").toLowerCase();
      const cid = (a.client_id || "").toLowerCase();
      return biz.includes(q) || cid.includes(q);
    });
  }, [expenses, expenseQuery]);

  const filteredBookings = useMemo(() => {
    let list = overview?.bookings || [];
    // Advisor filter — separates diagnostic calls (Kalib/John) from advisory
    // calls with Chris. Diagnostic bookings have no `advisor` field.
    if (advisorFilter === "chris") list = list.filter((b) => b.advisor === "chris");
    if (advisorFilter === "diagnostic") list = list.filter((b) => !b.advisor);
    if (bookingFilter === "all" || !list.length) return list;
    // Compare in the local timezone — a booking is "today" if its slot's
    // yyyy-mm-dd matches today's yyyy-mm-dd. Otherwise upcoming vs past is
    // decided by the current wall-clock timestamp.
    const now = Date.now();
    const today = new Date();
    const todayKey = `${today.getFullYear()}-${String(today.getMonth()+1).padStart(2,"0")}-${String(today.getDate()).padStart(2,"0")}`;
    return list.filter((b) => {
      if (!b.slot_iso) return bookingFilter === "all";
      const d = new Date(b.slot_iso);
      const bKey = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
      if (bookingFilter === "today") return bKey === todayKey;
      if (bookingFilter === "upcoming") return d.getTime() >= now;
      if (bookingFilter === "past") return d.getTime() < now;
      return true;
    });
  }, [overview, bookingFilter, advisorFilter]);

  const bookingCounts = useMemo(() => {
    const list = overview?.bookings || [];
    const now = Date.now();
    const today = new Date();
    const todayKey = `${today.getFullYear()}-${String(today.getMonth()+1).padStart(2,"0")}-${String(today.getDate()).padStart(2,"0")}`;
    let t = 0, u = 0, p = 0, chris = 0, diagnostic = 0;
    for (const b of list) {
      if (b.advisor === "chris") chris += 1;
      else diagnostic += 1;
      if (!b.slot_iso) continue;
      const d = new Date(b.slot_iso);
      const bKey = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
      if (bKey === todayKey) t += 1;
      if (d.getTime() >= now) u += 1; else p += 1;
    }
    return { all: list.length, today: t, upcoming: u, past: p, chris, diagnostic };
  }, [overview]);

  const filteredPredictions = useMemo(() => {
    const list = overview?.predictions || [];
    const q = predictionQuery.trim().toLowerCase();
    if (!q) return list;
    return list.filter((p) => {
      const inp = p.input || {};
      const res = p.result || {};
      return (
        (inp.company || "").toLowerCase().includes(q) ||
        (inp.industry || "").toLowerCase().includes(q) ||
        (inp.bbb_rating || "").toLowerCase().includes(q) ||
        (res.projected_bbb || "").toLowerCase().includes(q) ||
        (res.risk_level || "").toLowerCase().includes(q)
      );
    });
  }, [overview, predictionQuery]);

  // Per-admin tab ordering + hide/show (drag to reorder; persisted per account).
  const orderedTabs = useMemo(() => {
    const isFounder = me?.role === "founder";
    const grants = me?.allowed_tabs; // array when a founder has customised this member
    const visible = TABS.filter((t) => {
      if (t.founderLock) return isFounder;       // permissions console: founders only
      if (isFounder || !me) return true;          // founders (and pre-auth) see everything
      if (Array.isArray(grants)) return grants.includes(t.id);  // explicit per-member grants
      return !t.sensitive;                        // default for members: all non-sensitive tabs
    });
    const byId = Object.fromEntries(visible.map((t) => [t.id, t]));
    const ordered = (tabOrder || []).map((id) => byId[id]).filter(Boolean);
    visible.forEach((t) => { if (!ordered.includes(t)) ordered.push(t); });
    // Hidden tabs stay visible only while editing the layout.
    if (!editLayout) return ordered.filter((t) => !hiddenTabs.includes(t.id));
    return ordered;
  }, [tabOrder, hiddenTabs, editLayout, me]);

  // If a member is sitting on a tab they no longer have access to, bounce them
  // to the first tab they can see.
  useEffect(() => {
    if (!orderedTabs.length) return;
    if (!orderedTabs.some((t) => t.id === tab)) setTab(orderedTabs[0].id);
  }, [orderedTabs, tab]);
  // Deep-link: a bookmarked ?leads=<filter> URL lands straight on the Prospector
  // → Outreach Engine with that funnel filter applied (beats the saved default tab).
  useEffect(() => {
    if (appliedDeepLink.current) return;
    try {
      const p = new URLSearchParams(window.location.search);
      if (p.get("leads")) {
        setTab("prospector");
        appliedDefault.current = true;
      }
    } catch { /* ignore */ }
    appliedDeepLink.current = true;
  }, []);
  // Land on the saved default tab once, after the dashboard loads.
  useEffect(() => {
    if (appliedDefault.current || !overview) return;
    if (defaultTab && TABS.some((t) => t.id === defaultTab) && !hiddenTabs.includes(defaultTab)) {
      setTab(defaultTab);
    }
    appliedDefault.current = true;
  }, [overview, defaultTab, hiddenTabs]);
  // Reliable keyboard/click reorder (drag-and-drop is flaky in iframes/trackpads).
  const moveTabBy = (id, dir) => {
    const ids = orderedTabs.map((t) => t.id);
    const i = ids.indexOf(id), j = i + dir;
    if (i < 0 || j < 0 || j >= ids.length) return;
    [ids[i], ids[j]] = [ids[j], ids[i]];
    persistTabOrder(ids);
  };
  // Pointer-based drag reorder — works with mouse AND touch, inside iframes,
  // where native HTML5 drag-and-drop silently fails. Reorders live as you drag,
  // persists once on release.
  const beginDrag = (e, id) => {
    if (!editLayout) return;
    if (e.target.closest && e.target.closest("[data-tab-controls]")) return; // let hide/star/arrows work
    setDragId(id);
    if (!tabOrder || tabOrder.length === 0) setTabOrder(orderedTabs.map((t) => t.id));
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* ignore */ }
  };
  const dragMove = (e) => {
    if (!dragId) return;
    const el = document.elementFromPoint(e.clientX, e.clientY);
    const li = el && el.closest && el.closest("[data-tabid]");
    const overId = li && li.getAttribute("data-tabid");
    if (!overId || overId === dragId) return;
    setTabOrder((prev) => {
      const ids = (prev && prev.length ? prev.slice() : orderedTabs.map((t) => t.id));
      const fi = ids.indexOf(dragId), ti = ids.indexOf(overId);
      if (fi < 0 || ti < 0) return prev;
      ids.splice(ti, 0, ids.splice(fi, 1)[0]);
      dragOrderRef.current = ids;
      return ids;
    });
  };
  const endDrag = (e) => {
    if (!dragId) return;
    try { e.currentTarget.releasePointerCapture(e.pointerId); } catch { /* ignore */ }
    setDragId(null);
    saveLayout({ tab_order: dragOrderRef.current || orderedTabs.map((t) => t.id) });
    dragOrderRef.current = null;
  };

  if (!secret || !overview) {
    // Render onboarding gate for logged-in non-founder members who haven't finished.
    if (me && me.role !== "founder" && (onboardingGate === "needed" || onboardingGate === "submitted")) {
      return (
        <StaffOnboarding
          user={me}
          onLogout={logout}
          onCompleted={() => {
            setOnboardingGate("cleared");
            const s = "jwt:" + me.username;
            setSecret(s);
            load(s);
          }}
        />
      );
    }
    if (me && onboardingGate === "checking") {
      return (
        <div className="min-h-screen bg-[#eae7de] flex items-center justify-center" data-testid="admin-onboarding-checking">
          <Loader2 className="h-6 w-6 animate-spin text-[#c9a961]" />
        </div>
      );
    }
    return (
      <AdminAuthGate
        secretLoading={loading}
        onSecretSubmit={(s) => {
          if (!s) return;
          localStorage.setItem("elevare_admin_secret", s);
          setInput(s);
          setSecret(s);
          // Kick the overview load immediately — the mount effect only fires
          // once, so a state change alone won't retrigger it.
          load(s);
        }}
        onAuthed={async (user) => {
          localStorage.setItem("elevare_admin_user", user.username);
          const cleared = await checkOnboarding(user);
          if (cleared) {
            setSecret("jwt:" + user.username);
            load("jwt:" + user.username);
          }
        }}
      />
    );
  }

  return (
    <div className="min-h-screen bg-[#eae7de] text-[#1e3d3a]">
      <header
        data-testid="admin-header"
        data-compact={headerCompact ? "true" : "false"}
        className={`border-b border-[#1e3d3a]/10 bg-white/70 backdrop-blur-md sticky top-0 z-20 transition-[height,box-shadow] duration-300 ease-out ${headerCompact ? "shadow-[0_6px_20px_-12px_rgba(30,61,58,0.35)]" : ""}`}
      >
        <div className={`max-w-[1400px] mx-auto px-6 lg:px-10 flex items-center justify-between transition-[height] duration-300 ease-out ${headerCompact ? "h-11" : "h-16"}`}>
          <div className="flex items-center gap-3">
            <ElevareMark size={headerCompact ? 28 : 44} />
            <span className={`font-head text-[#1e3d3a] transition-all duration-300 ${headerCompact ? "text-sm" : "text-lg"}`}>Elevare</span>
            <span className={`uppercase tracking-[0.28em] text-[#c9a961] transition-all duration-300 ${headerCompact ? "text-[10px]" : "text-[11px]"}`}>Admin</span>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/" data-testid="admin-back-site" className="text-xs uppercase tracking-[0.25em] text-[#1e3d3a]/60 hover:text-[#1e3d3a] transition-colors duration-300">Site</Link>
            <AdminProfileMenu onLogout={logout} />
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10">
        {/* Sidebar (KPIs + tabs) + tab content */}
        <div className={`grid grid-cols-1 gap-6 md:gap-8 ${focusMode ? "md:grid-cols-1" : "md:grid-cols-[240px_minmax(0,1fr)]"}`}>
          {/* Sidebar */}
          <aside
            data-testid="admin-sidebar"
            className={`md:sticky md:top-6 md:self-start md:max-h-[calc(100vh-3rem)] md:overflow-y-auto -mx-4 md:mx-0 md:pr-1 space-y-4 ${focusMode ? "hidden" : ""}`}
            aria-label="Admin navigation"
          >
            {/* Compact KPI stack — dense, glanceable, click a number to override */}
            <div className="hidden md:block rounded-xl border border-[#1e3d3a]/12 bg-white/80 backdrop-blur-sm px-3 py-3">
              <p className="text-[10px] uppercase tracking-[0.24em] text-[#1e3d3a]/55 mb-2 px-1">Snapshot · tap a number to edit</p>
              <ul className="divide-y divide-[#1e3d3a]/6">
                {[
                  { key: "leads", label: "Leads", live: counts.leads, icon: Mail, testid: "kpi-leads" },
                  { key: "bookings", label: "Bookings", live: counts.bookings, icon: Calendar, testid: "kpi-bookings" },
                  { key: "predictions", label: "Predictions", live: counts.predictions, icon: Sparkles, testid: "kpi-predictions" },
                  { key: "payments", label: "Payments", live: counts.payments, icon: DollarSign, testid: "kpi-payments" },
                  { key: "expenses", label: "Expense analyses", live: overview.expense_count, icon: FileText, testid: "kpi-expenses" },
                ].map((k) => {
                  const overridden = snapOverrides[k.key] !== undefined && snapOverrides[k.key] !== null;
                  const shown = overridden ? snapOverrides[k.key] : k.live;
                  const isEditing = snapEditKey === k.key;
                  return (
                    <li key={k.key} data-testid={k.testid} className="flex items-center gap-2.5 py-2 px-1">
                      <k.icon className="h-3.5 w-3.5 text-[#c9a961] shrink-0" strokeWidth={1.6} />
                      <span className="text-[11px] uppercase tracking-[0.2em] text-[#1e3d3a]/65 truncate">{k.label}</span>
                      {isEditing ? (
                        <span className="ml-auto flex items-center gap-1">
                          <input
                            autoFocus
                            type="number"
                            data-testid={`kpi-input-${k.key}`}
                            value={snapDraft}
                            onChange={(e) => setSnapDraft(e.target.value)}
                            onKeyDown={(e) => { if (e.key === "Enter") saveSnapOverride(k.key, snapDraft); if (e.key === "Escape") setSnapEditKey(null); }}
                            placeholder={`${k.live}`}
                            className="w-16 rounded border border-[#c9a961] bg-white px-1.5 py-0.5 text-sm text-right text-[#1e3d3a] focus:outline-none"
                          />
                          <button type="button" data-testid={`kpi-save-${k.key}`} onClick={() => saveSnapOverride(k.key, snapDraft)} title="Save" className="text-[#4d5a24] hover:text-[#6b7c3a]"><Check className="h-3.5 w-3.5" /></button>
                          <button type="button" data-testid={`kpi-clear-${k.key}`} onClick={() => saveSnapOverride(k.key, "")} title="Reset to live count" className="text-[#1e3d3a]/40 hover:text-[#b23a48]"><X className="h-3.5 w-3.5" /></button>
                        </span>
                      ) : (
                        <button
                          type="button"
                          data-testid={`kpi-edit-${k.key}`}
                          onClick={() => { setSnapEditKey(k.key); setSnapDraft(overridden ? String(snapOverrides[k.key]) : ""); }}
                          title="Click to override this number"
                          className="ml-auto flex items-center gap-1 group"
                        >
                          <span className={`font-head text-lg font-light tabular-nums leading-none ${overridden ? "text-[#a3823f]" : "text-[#1e3d3a]"}`}>{shown}</span>
                          {overridden && <span className="h-1.5 w-1.5 rounded-full bg-[#c9a961]" title="Manual override" />}
                          <Pencil className="h-3 w-3 text-[#1e3d3a]/0 group-hover:text-[#1e3d3a]/40 transition-colors duration-200" />
                        </button>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>

            {/* Mobile KPI strip — horizontal, tight */}
            <div className="md:hidden grid grid-cols-5 gap-1.5 -mx-4 px-4 overflow-x-auto pb-1">
              {[
                { label: "Leads", value: counts.leads, testid: "kpi-leads-mobile" },
                { label: "Bookings", value: counts.bookings, testid: "kpi-bookings-mobile" },
                { label: "Predictions", value: counts.predictions, testid: "kpi-predictions-mobile" },
                { label: "Payments", value: counts.payments, testid: "kpi-payments-mobile" },
                { label: "Expenses", value: overview.expense_count, testid: "kpi-expenses-mobile" },
              ].map((k) => (
                <div key={k.label} data-testid={k.testid} className="rounded-lg border border-[#1e3d3a]/10 bg-white px-2 py-2 text-center">
                  <div className="font-head text-lg font-light text-[#1e3d3a] tabular-nums leading-none">{k.value}</div>
                  <div className="text-[8.5px] uppercase tracking-[0.18em] text-[#1e3d3a]/55 mt-1 truncate">{k.label}</div>
                </div>
              ))}
            </div>

            {/* Team performance — per-founder + together roll-up */}
            <div className="hidden md:block">
              <TeamPerformanceCard />
            </div>
            <PendingApprovalsCard />
            <OnboardingReviewCard />
            <AssessmentsOverviewCard />

            {/* Tab rail */}
            <nav data-testid="admin-tab-rail" aria-label="Admin sections">
              <div className="flex items-center justify-between px-1 pb-1.5">
                <button type="button" data-testid="admin-edit-layout-toggle"
                  onClick={() => setEditLayout((v) => {
                    if (v) { saveLayout({ tab_order: tabOrder, hidden_tabs: hiddenTabs, default_tab: defaultTab }); toast.success(cloudSync ? "Layout saved to your account — it'll stay this way next time you sign in." : "Layout saved on this device."); }
                    return !v;
                  })}
                  className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.14em] transition-colors duration-300 ${editLayout ? "text-[#a3823f]" : "text-[#1e3d3a]/45 hover:text-[#a3823f]"}`}>
                  {editLayout ? <Save className="h-3 w-3" /> : <GripVertical className="h-3 w-3" />} {editLayout ? "Save & done" : "Customize tabs"}
                </button>
                {editLayout && (
                  <button type="button" data-testid="admin-reset-layout" onClick={resetTabOrder}
                    className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.14em] text-[#1e3d3a]/45 hover:text-[#b23a48] transition-colors duration-300">
                    <RotateCcw className="h-3 w-3" /> Reset
                  </button>
                )}
              </div>
              {editLayout && (
                <p className="px-1 pb-2 text-[10px] text-[#1e3d3a]/45 leading-snug" data-testid="admin-layout-hint">
                  Hold and drag a tab to move it — or use <ChevronUp className="inline h-3 w-3" /><ChevronDown className="inline h-3 w-3" /> · <Eye className="inline h-3 w-3" /> hide/show · <Star className="inline h-3 w-3" /> set your default landing tab{cloudSync ? " · saved to your account" : ""}
                </p>
              )}
              <ul className="flex md:block gap-1 md:gap-0 overflow-x-auto md:overflow-visible border-b md:border-b-0 md:border-r border-[#1e3d3a]/12">
                {orderedTabs.map((t) => {
                  const active = tab === t.id;
                  const isHidden = hiddenTabs.includes(t.id);
                  return (
                    <li key={t.id} data-tabid={t.id} className={`relative shrink-0 md:shrink md:mb-1 ${editLayout && isHidden ? "opacity-45" : ""} ${editLayout && dragId === t.id ? "opacity-60 ring-2 ring-[#c9a961] rounded-lg z-10" : ""} ${editLayout ? "select-none flex items-center gap-1 rounded-lg md:rounded-r-none md:rounded-l-lg bg-[#1e3d3a]/[0.02]" : ""}`}
                      style={editLayout ? { touchAction: "none" } : undefined}
                      onPointerDown={(e) => beginDrag(e, t.id)}
                      onPointerMove={dragMove}
                      onPointerUp={endDrag}
                      onPointerCancel={endDrag}
                    >
                      <button
                        data-testid={`tab-${t.id}`}
                        onClick={() => { if (!editLayout) setTab(t.id); }}
                        className={`flex items-center gap-2.5 px-3.5 py-2.5 text-sm font-medium rounded-lg md:rounded-r-none md:rounded-l-lg text-left border-b-2 md:border-b-0 md:border-r-2 transition-all duration-300 ${editLayout ? "flex-1 min-w-0 cursor-grab active:cursor-grabbing" : "w-full"} ${
                          active
                            ? "text-[#1e3d3a] bg-[#c9a961]/12 border-[#c9a961]"
                            : "text-[#1e3d3a]/55 border-transparent hover:text-[#1e3d3a] hover:bg-[#1e3d3a]/4"
                        }`}
                      >
                        {editLayout && <GripVertical className="h-3.5 w-3.5 shrink-0 text-[#c9a961]" />}
                        <t.icon className={`h-4 w-4 shrink-0 ${active ? "text-[#a3823f]" : ""}`} />
                        <span className={editLayout ? "truncate" : "whitespace-nowrap md:whitespace-normal"}>{t.label}</span>
                        {defaultTab === t.id && !editLayout && <Star className="h-3 w-3 shrink-0 text-[#c9a961]" fill="currentColor" />}
                        {!editLayout && counts[t.id] !== undefined && (
                          <span className={`ml-auto text-[10px] tabular-nums rounded-full px-1.5 py-0.5 ${active ? "bg-[#1e3d3a] text-[#c9a961]" : "bg-[#1e3d3a]/8 text-[#1e3d3a]/70"}`}>
                            {counts[t.id]}
                          </span>
                        )}
                      </button>
                      {editLayout && (
                        <div data-tab-controls className="flex items-center gap-0.5 shrink-0 pr-1.5">
                          <button type="button" title="Move up" data-testid={`tab-up-${t.id}`}
                            onClick={(e) => { e.stopPropagation(); moveTabBy(t.id, -1); }}
                            className="text-[#1e3d3a]/40 hover:text-[#a3823f] transition-colors duration-200">
                            <ChevronUp className="h-4 w-4" />
                          </button>
                          <button type="button" title="Move down" data-testid={`tab-down-${t.id}`}
                            onClick={(e) => { e.stopPropagation(); moveTabBy(t.id, 1); }}
                            className="text-[#1e3d3a]/40 hover:text-[#a3823f] transition-colors duration-200">
                            <ChevronDown className="h-4 w-4" />
                          </button>
                          <button type="button" title={isHidden ? "Show tab" : "Hide tab"} data-testid={`tab-hide-${t.id}`}
                            onClick={(e) => { e.stopPropagation(); toggleHiddenTab(t.id); }}
                            className="text-[#1e3d3a]/40 hover:text-[#a3823f] transition-colors duration-200">
                            {isHidden ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                          </button>
                          <button type="button" title="Set as default landing tab" data-testid={`tab-default-${t.id}`}
                            onClick={(e) => { e.stopPropagation(); setAsDefault(t.id); }}
                            className={`transition-colors duration-200 ${defaultTab === t.id ? "text-[#c9a961]" : "text-[#1e3d3a]/40 hover:text-[#a3823f]"}`}>
                            <Star className="h-3.5 w-3.5" fill={defaultTab === t.id ? "currentColor" : "none"} />
                          </button>
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            </nav>
          </aside>

          {/* Tab content */}
          <div className="min-w-0">
          {/* Full-screen / focus toolbar */}
          <div className={`mb-4 flex items-center gap-2 ${focusMode ? "sticky top-2 z-20" : ""}`}>
            <button
              type="button"
              data-testid="admin-focus-toggle"
              onClick={toggleFocus}
              title={focusMode ? "Show the sidebar" : "Hide the sidebar for a full-width view"}
              className="inline-flex items-center gap-1.5 rounded-full border border-[#1e3d3a]/15 bg-white/90 backdrop-blur px-3 py-1.5 text-[11px] font-medium uppercase tracking-[0.12em] text-[#1e3d3a]/70 hover:border-[#c9a961] hover:text-[#1e3d3a] transition-colors duration-300 shrink-0"
            >
              {focusMode ? <PanelLeft className="h-3.5 w-3.5" /> : <Maximize2 className="h-3.5 w-3.5" />}
              {focusMode ? "Show tabs" : "Full screen"}
            </button>
            {focusMode && (
              <div className="flex items-center gap-1 overflow-x-auto rounded-full border border-[#1e3d3a]/12 bg-white/90 backdrop-blur px-1.5 py-1" data-testid="admin-focus-tab-strip">
                {orderedTabs.map((t) => {
                  const on = tab === t.id;
                  return (
                    <button
                      key={t.id}
                      type="button"
                      data-testid={`focus-tab-${t.id}`}
                      onClick={() => setTab(t.id)}
                      title={t.label}
                      className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium whitespace-nowrap transition-colors duration-200 ${on ? "bg-[#c9a961]/15 text-[#1e3d3a]" : "text-[#1e3d3a]/55 hover:text-[#1e3d3a] hover:bg-[#1e3d3a]/5"}`}
                    >
                      <t.icon className={`h-3.5 w-3.5 shrink-0 ${on ? "text-[#a3823f]" : ""}`} />
                      <span className="hidden sm:inline">{t.label}</span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
          {tab === "leads" && (
            <div data-testid="tab-content-leads" className="space-y-3">
              {/* Compact funnel — computed client-side from the loaded leads. */}
              {overview.leads.length > 0 && (() => {
                const c = { new: 0, nurture: 0, won: 0, lost: 0 };
                for (const l of overview.leads) {
                  const s = l.status || (l.converted_client_id ? "won" : "new");
                  c[s] = (c[s] || 0) + 1;
                }
                const total = overview.leads.length;
                const conv = total ? Math.round(1000 * c.won / total) / 10 : 0;
                const chip = (k, tone) => (
                  <span key={k} className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.22em] px-2.5 py-1 rounded-full ${tone}`}>
                    <span className="tabular-nums font-semibold">{c[k] || 0}</span> {k}
                  </span>
                );
                return (
                  <div data-testid="leads-funnel" className="flex flex-wrap items-center gap-2 rounded-xl border border-[#1e3d3a]/10 bg-white px-4 py-2">
                    <span className="text-[10px] uppercase tracking-[0.22em] text-[#1e3d3a]/55">Funnel</span>
                    {chip("new", "bg-black/6 text-black/60")}
                    {chip("nurture", "bg-[#c9a961]/18 text-[#a3823f]")}
                    {chip("won", "bg-[#6b7c3a]/15 text-[#4d5a24]")}
                    {chip("lost", "bg-[#b23a48]/10 text-[#b23a48]")}
                    <span
                      data-testid="leads-conversion-pct"
                      className="ml-auto text-[10px] uppercase tracking-[0.22em] text-[#1e3d3a]"
                      title={`${c.won} won out of ${total}`}
                    >
                      Conversion <span className="font-head text-base ml-1">{conv}%</span>
                    </span>
                  </div>
                );
              })()}
              {selectedLeads.size > 0 && (
                <div data-testid="leads-bulk-bar" className="flex items-center justify-between gap-3 rounded-2xl border border-[#c9a961]/50 bg-[#c9a961]/10 px-5 py-3">
                  <span className="text-sm text-[#1e3d3a]">
                    <span className="font-medium tabular-nums">{selectedLeads.size}</span> selected
                  </span>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      data-testid="leads-export-selected"
                      onClick={exportLeadsCsv}
                      className="inline-flex items-center gap-1.5 rounded-full border border-[#1e3d3a]/15 bg-white px-4 py-1.5 text-[11px] uppercase tracking-[0.2em] text-[#1e3d3a] hover:border-[#c9a961] hover:text-[#a3823f] transition-colors duration-300"
                    >
                      <FileDown className="h-3.5 w-3.5" /> Export CSV
                    </button>
                    <button
                      type="button"
                      data-testid="leads-bulk-delete"
                      onClick={bulkDeleteLeads}
                      className="inline-flex items-center gap-1.5 rounded-full bg-[#b23a48] text-white px-4 py-1.5 text-[11px] uppercase tracking-[0.2em] hover:bg-[#8f2d38] transition-colors duration-300"
                    >
                      <Trash2 className="h-3.5 w-3.5" /> Delete selected
                    </button>
                    <button
                      type="button"
                      data-testid="leads-selection-clear"
                      onClick={() => setSelectedLeads(new Set())}
                      className="text-[11px] uppercase tracking-[0.2em] text-[#1e3d3a]/55 hover:text-[#1e3d3a] transition-colors duration-300"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
              {selectedLeads.size === 0 && (
                <div className="flex justify-end items-center gap-2">
                  <button
                    type="button"
                    data-testid="leads-add-open"
                    onClick={() => setShowAddLead(true)}
                    className="inline-flex items-center gap-1.5 rounded-full bg-[#1e3d3a] text-white px-4 py-1.5 text-[11px] uppercase tracking-[0.2em] hover:bg-[#c9a961] hover:text-[#1e3d3a] transition-colors duration-300"
                  >
                    <Plus className="h-3.5 w-3.5" /> Add lead
                  </button>
                  {overview.leads.length > 0 && (
                    <button
                      type="button"
                      data-testid="leads-export-all"
                      onClick={exportLeadsCsv}
                      className="inline-flex items-center gap-1.5 rounded-full border border-[#1e3d3a]/15 bg-white px-4 py-1.5 text-[11px] uppercase tracking-[0.2em] text-[#1e3d3a] hover:border-[#c9a961] hover:text-[#a3823f] transition-colors duration-300"
                    >
                      <FileDown className="h-3.5 w-3.5" /> Export all as CSV
                    </button>
                  )}
                </div>
              )}
              <div className="rounded-2xl border border-[#1e3d3a]/12 bg-white overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-[#eae7de] text-[10px] uppercase tracking-[0.25em] text-[#1e3d3a]/60">
                  <tr>
                    <th className="text-left px-5 py-3 w-10">
                      <input
                        type="checkbox"
                        data-testid="leads-select-all"
                        onChange={toggleAllLeads}
                        checked={overview.leads.length > 0 && selectedLeads.size === overview.leads.length}
                        className="h-4 w-4 accent-[#1e3d3a] cursor-pointer"
                      />
                    </th>
                    <th className="text-left px-5 py-3">Received</th>
                    <th className="text-left px-5 py-3">Name</th>
                    <th className="text-left px-5 py-3">Email</th>
                    <th className="text-left px-5 py-3">Company</th>
                    <th className="text-left px-5 py-3">
                      <button
                        type="button"
                        data-testid="leads-sort-health"
                        onClick={() => setLeadSort((s) => (s === "health" ? "recent" : "health"))}
                        className={`inline-flex items-center gap-1 uppercase tracking-[0.25em] transition-colors ${leadSort === "health" ? "text-[#a3823f]" : "hover:text-[#1e3d3a]"}`}
                        title="Sort by Business Health Score"
                      >
                        Health {leadSort === "health" ? "▾" : "⇅"}
                      </button>
                    </th>
                    <th className="text-left px-5 py-3">Message</th>
                    <th className="text-right px-5 py-3 w-16"></th>
                  </tr>
                </thead>
                <tbody>
                  {overview.leads.length === 0 && <tr><td colSpan="8" className="text-center text-[#1e3d3a]/40 py-8">No leads yet.</td></tr>}
                  {[...overview.leads].sort((a, b) => leadSort === "health" ? ((b.health_score ?? -1) - (a.health_score ?? -1)) : 0).map((l, i) => (
                    <tr
                      key={l.id || i}
                      data-testid={`lead-row-${l.id || i}`}
                      onClick={() => l.id && setOpenLead(l)}
                      className={`border-t border-[#1e3d3a]/8 group cursor-pointer hover:bg-[#c9a961]/6 transition-colors duration-200 ${selectedLeads.has(l.id) ? "bg-[#c9a961]/6" : ""}`}
                    >
                      <td className="px-5 py-3" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          data-testid={`lead-check-${l.id}`}
                          disabled={!l.id}
                          checked={selectedLeads.has(l.id)}
                          onChange={() => toggleLead(l.id)}
                          className="h-4 w-4 accent-[#1e3d3a] cursor-pointer"
                        />
                      </td>
                      <td className="px-5 py-3 text-[#1e3d3a]/70 whitespace-nowrap">{fmtDate(l.created_at)}</td>
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-2">
                          <span>{l.name || "—"}</span>
                          <PlanSentChip req={planSent[(l.email || "").toLowerCase()]} testid={`lead-plan-chip-${l.id}`} />
                          {l.converted_client_id && (
                            <span
                              data-testid={`lead-crm-chip-${l.id}`}
                              className="inline-flex items-center gap-1 text-[9px] uppercase tracking-[0.2em] bg-[#6b7c3a]/12 text-[#4d5a24] px-1.5 py-0.5 rounded-full"
                              title="This lead has been added to the CRM"
                            >
                              In CRM
                            </span>
                          )}
                          {l.records_requested_at && !l.converted_client_id && (
                            <span
                              className="inline-flex items-center gap-1 text-[9px] uppercase tracking-[0.2em] bg-[#c9a961]/18 text-[#a3823f] px-1.5 py-0.5 rounded-full"
                              title={`Records requested ${fmtDate(l.records_requested_at)}`}
                            >
                              Records sent
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-5 py-3" onClick={(e) => e.stopPropagation()}>
                        <a href={`mailto:${l.email}`} className="text-[#a3823f] hover:text-[#1e3d3a] transition-colors duration-300">{l.email}</a>
                      </td>
                      <td className="px-5 py-3 text-[#1e3d3a]/70">{l.company || "—"}</td>
                      <td className="px-5 py-3" data-testid={`lead-health-${l.id}`}>
                        {typeof l.health_score === "number" ? (() => {
                          const m = scoreMeta(l.health_score);
                          return (
                            <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold whitespace-nowrap" style={{ backgroundColor: m.color + "1a", color: m.color }}>
                              <span className="h-1.5 w-1.5 rounded-full" style={{ background: m.color }} />{l.health_score} · {l.health_grade || m.grade}
                            </span>
                          );
                        })() : <span className="text-[#1e3d3a]/30">—</span>}
                      </td>
                      <td className="px-5 py-3 text-[#1e3d3a]/70 max-w-md truncate">{l.message || "—"}</td>
                      <td className="px-5 py-3 text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                        {me?.role === "founder" && l.email && (
                          <button
                            type="button"
                            data-testid={`lead-send-plan-${l.id}`}
                            onClick={() => setProposeFor({ name: l.name || "", email: l.email, business_name: l.company || "" })}
                            title={planSent[(l.email || "").toLowerCase()] ? "A payment link is already out — send another only if needed" : "Send this lead a plan + payment link"}
                            className={`inline-flex h-8 items-center gap-1 rounded-full px-2.5 text-[10px] uppercase tracking-[0.14em] transition-colors duration-300 ${planSent[(l.email || "").toLowerCase()] ? "text-[#1e3d3a]/30 hover:text-[#1e3d3a]/60" : "text-[#1e3d3a]/55 hover:text-[#1e3d3a] hover:bg-[#c9a961]/15"}`}
                          >
                            <CreditCard className="h-3.5 w-3.5" strokeWidth={1.6} /> {planSent[(l.email || "").toLowerCase()] ? "Resend plan" : "Send plan"}
                          </button>
                        )}
                        <button
                          type="button"
                          data-testid={`delete-lead-${l.id}`}
                          onClick={() => deleteLead(l)}
                          disabled={!l.id}
                          title="Remove lead"
                          className="inline-flex h-8 w-8 items-center justify-center rounded-full text-[#1e3d3a]/40 hover:text-[#b23a48] hover:bg-[#b23a48]/8 transition-colors duration-300 disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          <Trash2 className="h-4 w-4" strokeWidth={1.6} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
              {showAddLead && (
                <AddLeadModal
                  draft={leadDraft}
                  setDraft={setLeadDraft}
                  saving={savingLead}
                  onClose={() => setShowAddLead(false)}
                  onSave={saveNewLead}
                />
              )}
              {openLead && (
                <LeadProfileDrawer
                  lead={openLead}
                  secret={secret}
                  onClose={() => setOpenLead(null)}
                  onConverted={(leadId, vault) => {
                    // Stamp the lead row locally so the "In CRM" chip appears
                    // instantly without waiting for a refetch.
                    setOverview((o) => o ? {
                      ...o,
                      leads: o.leads.map((x) => x.id === leadId
                        ? { ...x, converted_client_id: vault?.client_id || x.email,
                             converted_at: new Date().toISOString() }
                        : x),
                    } : o);
                    // Reflect the change in the open drawer too.
                    setOpenLead((cur) => cur && cur.id === leadId
                      ? { ...cur, converted_client_id: vault?.client_id || cur.email,
                           converted_at: new Date().toISOString() }
                      : cur);
                  }}
                  onBooked={(booking) => {
                    // Optimistically prepend the new booking so the Bookings tab
                    // reflects it without a refresh.
                    setOverview((o) => o ? {
                      ...o,
                      bookings: [booking, ...(o.bookings || [])],
                    } : o);
                  }}
                  onCallLogged={(leadId, entry, lastOutcome) => {
                    // Prepend the new call-log entry to both the row in the
                    // overview and the drawer's local copy so the history
                    // renders immediately.
                    setOverview((o) => o ? {
                      ...o,
                      leads: o.leads.map((x) => x.id === leadId
                        ? { ...x,
                            call_logs: [entry, ...((x.call_logs) || [])],
                            last_call_outcome: lastOutcome,
                            last_call_at: entry?.at }
                        : x),
                    } : o);
                    setOpenLead((cur) => cur && cur.id === leadId
                      ? { ...cur,
                          call_logs: [entry, ...((cur.call_logs) || [])],
                          last_call_outcome: lastOutcome,
                          last_call_at: entry?.at }
                      : cur);
                  }}
                  onStatusChanged={(leadId, updated) => {
                    setOverview((o) => o ? {
                      ...o,
                      leads: o.leads.map((x) => x.id === leadId ? { ...x, ...updated } : x),
                    } : o);
                    setOpenLead((cur) => cur && cur.id === leadId ? { ...cur, ...updated } : cur);
                  }}
                />
              )}
            </div>
          )}

          {tab === "outreach-contacts" && (
            <OutreachContacts onLeadsChanged={() => load(secret)} />
          )}

          {tab === "bookings" && (
            <div data-testid="tab-content-bookings" className="space-y-3">
              <GoogleCalendarConnect />
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <span className="text-[10px] uppercase tracking-[0.24em] text-[#1e3d3a]/55">
                  {filteredBookings.length} of {overview.bookings.length} booking{overview.bookings.length === 1 ? "" : "s"}
                </span>
                <div className="flex items-center gap-2">
                  <div className="inline-flex rounded-full border border-[#1e3d3a]/15 bg-white p-0.5">
                    {[
                      { id: "list", label: "List", Icon: LayoutList },
                      { id: "calendar", label: "Calendar", Icon: CalendarDays },
                    ].map(({ id, label, Icon }) => (
                      <button
                        key={id}
                        type="button"
                        data-testid={`booking-view-${id}`}
                        onClick={() => setBookingView(id)}
                        className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[10px] uppercase tracking-[0.2em] transition-colors duration-300 ${
                          bookingView === id ? "bg-[#1e3d3a] text-white" : "text-[#1e3d3a]/55 hover:text-[#1e3d3a]"
                        }`}
                      >
                        <Icon className="h-3 w-3" strokeWidth={1.8} />
                        {label}
                      </button>
                    ))}
                  </div>
                  <button
                    type="button"
                    data-testid="new-booking-toggle"
                    onClick={() => setShowNewBooking((v) => !v)}
                    className="inline-flex items-center gap-1.5 rounded-full bg-[#1e3d3a] text-white px-4 py-1.5 text-[11px] uppercase tracking-[0.2em] font-semibold hover:bg-[#c9a961] hover:text-[#1e3d3a] transition-colors duration-300"
                  >
                    {showNewBooking ? <X className="h-3.5 w-3.5" /> : <Plus className="h-3.5 w-3.5" />}
                    {showNewBooking ? "Cancel" : "New booking"}
                  </button>
                </div>
              </div>

              {/* Filter chips — Today / Upcoming / Past / All. */}
              <div data-testid="booking-filter-chips" className="flex gap-1.5 flex-wrap">
                {[
                  { id: "today", label: "Today", count: bookingCounts.today },
                  { id: "upcoming", label: "Upcoming", count: bookingCounts.upcoming },
                  { id: "past", label: "Past", count: bookingCounts.past },
                  { id: "all", label: "All", count: bookingCounts.all },
                ].map((c) => {
                  const active = bookingFilter === c.id;
                  return (
                    <button
                      key={c.id}
                      type="button"
                      data-testid={`booking-filter-${c.id}`}
                      onClick={() => setBookingFilter(c.id)}
                      className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[10px] uppercase tracking-[0.22em] transition-colors duration-300 ${
                        active
                          ? "bg-[#1e3d3a] text-white"
                          : "border border-[#1e3d3a]/15 text-[#1e3d3a]/60 hover:text-[#1e3d3a] hover:border-[#c9a961]"
                      }`}
                    >
                      {c.label}
                      <span className={`tabular-nums text-[9px] rounded-full px-1.5 ${active ? "bg-[#c9a961] text-[#1e3d3a]" : "bg-[#1e3d3a]/8 text-[#1e3d3a]/60"}`}>
                        {c.count}
                      </span>
                    </button>
                  );
                })}
              </div>

              {/* Advisor filter — split diagnostic (Kalib/John) from advisory
                  (Chris). Only surfaces once at least one Chris booking exists,
                  so it stays out of the way otherwise. */}
              {bookingCounts.chris > 0 && (
                <div data-testid="booking-advisor-filter-chips" className="flex gap-1.5 flex-wrap">
                  {[
                    { id: "all", label: "Both", count: bookingCounts.all },
                    { id: "diagnostic", label: "Diagnostic", count: bookingCounts.diagnostic },
                    { id: "chris", label: "Chris · Advisor", count: bookingCounts.chris },
                  ].map((c) => {
                    const active = advisorFilter === c.id;
                    const isChris = c.id === "chris";
                    return (
                      <button
                        key={c.id}
                        type="button"
                        data-testid={`booking-advisor-filter-${c.id}`}
                        onClick={() => setAdvisorFilter(c.id)}
                        className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[10px] uppercase tracking-[0.22em] transition-colors duration-300 ${
                          active
                            ? isChris
                              ? "bg-[#c9a961] text-[#1e3d3a]"
                              : "bg-[#1e3d3a] text-white"
                            : "border border-[#1e3d3a]/15 text-[#1e3d3a]/60 hover:text-[#1e3d3a] hover:border-[#c9a961]"
                        }`}
                      >
                        {c.label}
                        <span className={`tabular-nums text-[9px] rounded-full px-1.5 ${
                          active
                            ? isChris ? "bg-[#1e3d3a] text-[#c9a961]" : "bg-[#c9a961] text-[#1e3d3a]"
                            : "bg-[#1e3d3a]/8 text-[#1e3d3a]/60"
                        }`}>
                          {c.count}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}

              {showNewBooking && (
                <form
                  data-testid="new-booking-form"
                  onSubmit={createBooking}
                  className="rounded-2xl border border-[#c9a961]/50 bg-[#c9a961]/8 p-5 grid grid-cols-1 md:grid-cols-2 gap-3"
                >
                  <label className="block text-xs">
                    <span className="text-[10px] uppercase tracking-[0.22em] text-[#1e3d3a]/60">Date</span>
                    <input
                      type="date"
                      required
                      data-testid="nb-date"
                      value={newBooking.date}
                      onChange={(e) => setNewBooking((v) => ({ ...v, date: e.target.value }))}
                      className="mt-1 w-full rounded-lg border border-[#1e3d3a]/15 bg-white px-3 py-2 text-sm text-[#1e3d3a] focus:border-[#c9a961] focus:outline-none focus:ring-2 focus:ring-[#c9a961]/20"
                    />
                  </label>
                  <label className="block text-xs">
                    <span className="text-[10px] uppercase tracking-[0.22em] text-[#1e3d3a]/60">Time (in TZ)</span>
                    <input
                      type="time"
                      required
                      data-testid="nb-time"
                      value={newBooking.time}
                      onChange={(e) => setNewBooking((v) => ({ ...v, time: e.target.value }))}
                      className="mt-1 w-full rounded-lg border border-[#1e3d3a]/15 bg-white px-3 py-2 text-sm text-[#1e3d3a] focus:border-[#c9a961] focus:outline-none focus:ring-2 focus:ring-[#c9a961]/20"
                    />
                  </label>
                  <label className="block text-xs md:col-span-2">
                    <span className="text-[10px] uppercase tracking-[0.22em] text-[#1e3d3a]/60">Time zone</span>
                    <select
                      data-testid="nb-tz"
                      value={newBooking.tz}
                      onChange={(e) => setNewBooking((v) => ({ ...v, tz: e.target.value }))}
                      className="mt-1 w-full rounded-lg border border-[#1e3d3a]/15 bg-white px-3 py-2 text-sm text-[#1e3d3a] focus:border-[#c9a961] focus:outline-none focus:ring-2 focus:ring-[#c9a961]/20"
                    >
                      {TIMEZONES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                  </label>
                  <label className="block text-xs">
                    <span className="text-[10px] uppercase tracking-[0.22em] text-[#1e3d3a]/60">Client name *</span>
                    <input
                      type="text"
                      required
                      data-testid="nb-name"
                      value={newBooking.name}
                      onChange={(e) => setNewBooking((v) => ({ ...v, name: e.target.value }))}
                      className="mt-1 w-full rounded-lg border border-[#1e3d3a]/15 bg-white px-3 py-2 text-sm text-[#1e3d3a] focus:border-[#c9a961] focus:outline-none focus:ring-2 focus:ring-[#c9a961]/20"
                    />
                  </label>
                  <label className="block text-xs">
                    <span className="text-[10px] uppercase tracking-[0.22em] text-[#1e3d3a]/60">Email *</span>
                    <input
                      type="email"
                      required
                      data-testid="nb-email"
                      value={newBooking.email}
                      onChange={(e) => setNewBooking((v) => ({ ...v, email: e.target.value }))}
                      className="mt-1 w-full rounded-lg border border-[#1e3d3a]/15 bg-white px-3 py-2 text-sm text-[#1e3d3a] focus:border-[#c9a961] focus:outline-none focus:ring-2 focus:ring-[#c9a961]/20"
                    />
                  </label>
                  <label className="block text-xs">
                    <span className="text-[10px] uppercase tracking-[0.22em] text-[#1e3d3a]/60">Company</span>
                    <input
                      type="text"
                      data-testid="nb-company"
                      value={newBooking.company}
                      onChange={(e) => setNewBooking((v) => ({ ...v, company: e.target.value }))}
                      className="mt-1 w-full rounded-lg border border-[#1e3d3a]/15 bg-white px-3 py-2 text-sm text-[#1e3d3a] focus:border-[#c9a961] focus:outline-none focus:ring-2 focus:ring-[#c9a961]/20"
                    />
                  </label>
                  <label className="block text-xs md:col-span-1">
                    <span className="text-[10px] uppercase tracking-[0.22em] text-[#1e3d3a]/60">Goal / note</span>
                    <input
                      type="text"
                      data-testid="nb-goal"
                      value={newBooking.goal}
                      onChange={(e) => setNewBooking((v) => ({ ...v, goal: e.target.value }))}
                      className="mt-1 w-full rounded-lg border border-[#1e3d3a]/15 bg-white px-3 py-2 text-sm text-[#1e3d3a] focus:border-[#c9a961] focus:outline-none focus:ring-2 focus:ring-[#c9a961]/20"
                    />
                  </label>
                  <button
                    type="submit"
                    data-testid="nb-submit"
                    disabled={creatingBooking}
                    className="md:col-span-2 mt-1 inline-flex items-center justify-center gap-2 rounded-full bg-[#1e3d3a] text-white py-2.5 text-xs uppercase tracking-[0.2em] font-semibold hover:bg-[#c9a961] hover:text-[#1e3d3a] transition-colors duration-300 disabled:opacity-60"
                  >
                    {creatingBooking ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
                    {creatingBooking ? "Creating…" : "Create booking"}
                  </button>
                </form>
              )}

              {bookingView === "calendar" && (
                <BookingsCalendar
                  bookings={filteredBookings}
                  tz={calendarTz}
                  onTzChange={setCalendarTz}
                  onSetAttendance={setBookingAttendance}
                  onSetTz={setBookingTz}
                  onEnsureMeet={ensureBookingMeet}
                  onAddParticipant={addBookingParticipant}
                  onRemoveParticipant={removeBookingParticipant}
                  myTz={detectTimezone()}
                />
              )}

              {bookingView === "list" && (
              <div className="rounded-2xl border border-[#1e3d3a]/12 bg-white overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-[#eae7de] text-[10px] uppercase tracking-[0.25em] text-[#1e3d3a]/60">
                  <tr>
                    <th className="text-left px-5 py-3">Slot</th>
                    <th className="text-left px-5 py-3">Name</th>
                    <th className="text-left px-5 py-3">Email</th>
                    <th className="text-left px-5 py-3">Company</th>
                    <th className="text-left px-5 py-3">Goal</th>
                    <th className="text-left px-5 py-3">Attendance</th>
                    <th className="text-right px-5 py-3 w-16"></th>
                  </tr>
                </thead>
                <tbody>
                  {overview.bookings.length === 0 && <tr><td colSpan="7" className="text-center text-[#1e3d3a]/40 py-8">No bookings yet.</td></tr>}
                  {overview.bookings.map((b, i) => {
                    // Find matching lead so clicking the row can open the profile drawer.
                    const relatedLead = (overview.leads || []).find(
                      (l) => (l.email || "").toLowerCase() === (b.email || "").toLowerCase(),
                    );
                    const bt = b.tz || "America/New_York";
                    const slot = b.slot_iso ? new Date(b.slot_iso) : null;
                    let bAbbr = "";
                    if (slot) {
                      try {
                        bAbbr = new Intl.DateTimeFormat("en-US", { timeZone: bt, timeZoneName: "short" })
                          .formatToParts(slot).find((p) => p.type === "timeZoneName")?.value || "";
                      } catch { bAbbr = ""; }
                    }
                    return (
                      <tr
                        key={b.id || i}
                        data-testid={`booking-row-${b.id || i}`}
                        onClick={() => relatedLead && setOpenLead(relatedLead)}
                        className={`border-t border-[#1e3d3a]/8 group transition-colors duration-200 ${
                          relatedLead ? "cursor-pointer hover:bg-[#c9a961]/6" : ""
                        }`}
                        title={relatedLead ? "Click to open client profile" : ""}
                      >
                        <td className="px-5 py-3 whitespace-nowrap">
                          {/* Date + time shown in the CLIENT's own booked time zone. */}
                          <div className="text-[#1e3d3a] font-medium">
                            {slot ? new Intl.DateTimeFormat(undefined, { timeZone: bt, weekday: "short", month: "short", day: "numeric" }).format(slot) : "—"}
                          </div>
                          <div className="text-[10px] uppercase tracking-[0.22em] text-[#a3823f] mt-0.5">
                            {slot ? `${new Intl.DateTimeFormat(undefined, { timeZone: bt, hour: "numeric", minute: "2-digit" }).format(slot)} ${bAbbr}` : ""}
                          </div>
                        </td>
                        <td className="px-5 py-3">
                          <div className="flex items-center gap-2">
                            <span>{b.name}</span>
                            {relatedLead?.converted_client_id && (
                              <span
                                data-testid={`booking-crm-chip-${b.id}`}
                                className="inline-flex items-center gap-1 text-[9px] uppercase tracking-[0.2em] bg-[#6b7c3a]/12 text-[#4d5a24] px-1.5 py-0.5 rounded-full"
                              >
                                In CRM
                              </span>
                            )}
                            {b.advisor === "chris" && (
                              <span
                                data-testid={`booking-advisor-chip-${b.id}`}
                                className="inline-flex items-center gap-1 text-[9px] uppercase tracking-[0.2em] bg-[#c9a961]/25 text-[#a3823f] border border-[#c9a961]/50 px-1.5 py-0.5 rounded-full"
                                title="Booked as an advisory call with Chris Myers"
                              >
                                Chris · Advisor
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-5 py-3" onClick={(e) => e.stopPropagation()}>
                          <a href={`mailto:${b.email}`} className="text-[#a3823f] hover:text-[#1e3d3a] transition-colors duration-300">{b.email}</a>
                        </td>
                        <td className="px-5 py-3 text-[#1e3d3a]/70">{b.company || "—"}</td>
                        <td className="px-5 py-3 text-[#1e3d3a]/70 max-w-md truncate">{b.goal || "—"}</td>
                        <td className="px-5 py-3 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                          <div className="flex items-center gap-3">
                            <label className="inline-flex items-center gap-1.5 cursor-pointer text-[9px] uppercase tracking-[0.16em] font-semibold text-[#4d5a24]">
                              <input
                                type="checkbox"
                                data-testid={`booking-showed-${b.id}`}
                                checked={b.attendance === "showed"}
                                onChange={() => setBookingAttendance(b, "showed")}
                                disabled={!b.id}
                                className="h-3.5 w-3.5 accent-[#6b7c3a] cursor-pointer"
                              />
                              Showed
                            </label>
                            <label className="inline-flex items-center gap-1.5 cursor-pointer text-[9px] uppercase tracking-[0.16em] font-semibold text-[#b23a48]">
                              <input
                                type="checkbox"
                                data-testid={`booking-noshow-${b.id}`}
                                checked={b.attendance === "no_show"}
                                onChange={() => setBookingAttendance(b, "no_show")}
                                disabled={!b.id}
                                className="h-3.5 w-3.5 accent-[#b23a48] cursor-pointer"
                              />
                              No-show
                            </label>
                          </div>
                        </td>
                        <td className="px-5 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                          <div className="inline-flex items-center gap-1">
                            {(b.meet_url || b.zoom_url) && (
                              <a
                                data-testid={`booking-meet-${b.id}`}
                                href={b.meet_url || b.zoom_url}
                                target="_blank"
                                rel="noreferrer"
                                title="Join Google Meet"
                                className="inline-flex h-8 w-8 items-center justify-center rounded-full text-[#1e3d3a]/40 hover:text-[#1e3d3a] hover:bg-[#c9a961]/15 transition-colors duration-300"
                              >
                                <Video className="h-4 w-4" strokeWidth={1.6} />
                              </a>
                            )}
                            <button
                              type="button"
                              data-testid={`delete-booking-${b.id}`}
                              onClick={() => deleteBooking(b)}
                              disabled={!b.id}
                              title="Cancel booking"
                              className="inline-flex h-8 w-8 items-center justify-center rounded-full text-[#1e3d3a]/40 hover:text-[#b23a48] hover:bg-[#b23a48]/8 transition-colors duration-300 disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                              <Trash2 className="h-4 w-4" strokeWidth={1.6} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              </div>
              )}
            </div>
          )}

          {tab === "predictions" && (
            <div data-testid="tab-content-predictions" className="space-y-4">
              <ClientBbbForecast />
              {overview.predictions.length > 0 && (
                <div className="flex items-center gap-3 rounded-2xl border border-[#1e3d3a]/12 bg-white px-5 py-3">
                  <Search className="h-4 w-4 text-[#1e3d3a]/50" strokeWidth={1.6} />
                  <input
                    data-testid="predictions-search"
                    type="text"
                    value={predictionQuery}
                    onChange={(e) => setPredictionQuery(e.target.value)}
                    placeholder="Search by company, industry, BBB rating or risk level…"
                    className="flex-1 bg-transparent text-sm text-[#1e3d3a] placeholder:text-[#1e3d3a]/40 focus:outline-none"
                  />
                  {predictionQuery && (
                    <button
                      type="button"
                      data-testid="predictions-search-clear"
                      onClick={() => setPredictionQuery("")}
                      className="text-[#1e3d3a]/45 hover:text-[#1e3d3a] transition-colors duration-300"
                      title="Clear"
                    >
                      <X className="h-4 w-4" strokeWidth={1.6} />
                    </button>
                  )}
                  <span className="text-[10px] uppercase tracking-[0.22em] text-[#1e3d3a]/50 tabular-nums">
                    {filteredPredictions.length} / {overview.predictions.length}
                  </span>
                </div>
              )}
              <BulkBar
                testid="predictions-bulk"
                total={filteredPredictions.length}
                selected={selectedPredictions.size}
                onSelectAll={toggleAllInSet(setSelectedPredictions, filteredPredictions.map((p) => p.id).filter(Boolean))}
                onClear={() => setSelectedPredictions(new Set())}
                onRemove={() => setBulkConfirm({ kind: "predictions", ids: [...selectedPredictions] })}
                label="forecasts"
              />
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {overview.predictions.length === 0 && <div className="text-center text-[#1e3d3a]/40 py-8">No AI forecasts yet.</div>}
              {overview.predictions.length > 0 && filteredPredictions.length === 0 && (
                <div className="col-span-full text-center text-[#1e3d3a]/45 text-sm py-10">No matches for “{predictionQuery}”.</div>
              )}
              {filteredPredictions.map((p, i) => {
                const inp = p.input || {};
                const res = p.result || {};
                const company = inp.company || p.business_name || "Untitled";
                const industry = inp.industry || p.industry || "—";
                const currentBBB = inp.bbb_rating || p.bbb_rating || "—";
                const projectedBBB = res.projected_bbb || "—";
                const same = currentBBB === projectedBBB;
                const isSel = p.id && selectedPredictions.has(p.id);
                return (
                  <div key={p.id || i} data-testid={`prediction-card-${i}`} className={`rounded-2xl border p-5 transition-colors duration-300 ${isSel ? "border-[#c9a961] bg-[#c9a961]/8" : "border-[#1e3d3a]/12 bg-white"}`}>
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex items-start gap-3 min-w-0">
                        {p.id && (
                          <input
                            type="checkbox"
                            data-testid={`prediction-select-${p.id}`}
                            checked={isSel}
                            onChange={() => togglePrediction(p.id)}
                            onClick={(e) => e.stopPropagation()}
                            className="mt-1 h-3.5 w-3.5 accent-[#a3823f] cursor-pointer"
                            title="Select for bulk remove"
                          />
                        )}
                        <div className="min-w-0">
                          <h4 className="font-head text-base text-[#1e3d3a] truncate">{company}</h4>
                          <p className="mt-1 text-[10px] uppercase tracking-[0.2em] text-[#1e3d3a]/55">{industry}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <span className="text-[10px] text-[#1e3d3a]/45 whitespace-nowrap">{fmtDate(p.created_at)}</span>
                        {p.id && (
                          <button
                            type="button"
                            data-testid={`prediction-delete-${p.id}`}
                            onClick={() => deletePrediction(p)}
                            title="Remove this forecast"
                            className="text-[#1e3d3a]/35 hover:text-[#b23a48] transition-colors duration-300"
                          >
                            <Trash2 className="h-3.5 w-3.5" strokeWidth={1.8} />
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="mt-4 flex items-center gap-3">
                      <div className="flex-1 rounded-xl border border-[#1e3d3a]/10 bg-[#eae7de]/60 px-4 py-3">
                        <div className="text-[9px] uppercase tracking-[0.22em] text-[#1e3d3a]/55">Current BBB</div>
                        <div className="mt-1 font-head text-2xl font-light text-[#1e3d3a]">{currentBBB}</div>
                      </div>
                      <span className="text-[#c9a961] text-xl font-light">→</span>
                      <div className={`flex-1 rounded-xl border px-4 py-3 ${projectedBBB === "—" ? "border-[#1e3d3a]/10 bg-[#eae7de]/40" : "border-[#c9a961]/40 bg-[#c9a961]/8"}`}>
                        <div className="text-[9px] uppercase tracking-[0.22em] text-[#a3823f]">After Elevare</div>
                        <div className={`mt-1 font-head text-2xl font-light ${projectedBBB === "—" ? "text-[#1e3d3a]/40" : "text-[#1e3d3a]"}`}>
                          {projectedBBB}
                          {!same && projectedBBB !== "—" && (
                            <span className="ml-2 align-middle text-[10px] uppercase tracking-[0.2em] text-[#4b7a3a]">Uplift</span>
                          )}
                        </div>
                      </div>
                    </div>
                    {res.projected_savings ? (
                      <p className="mt-3 text-xs text-[#1e3d3a]/60">
                        Projected savings <span className="text-[#4b7a3a] font-medium tabular-nums">${Number(res.projected_savings).toLocaleString()}/yr</span>
                        {res.risk_level && <span className="ml-3 text-[#1e3d3a]/45">· Risk {res.risk_level}</span>}
                      </p>
                    ) : null}
                  </div>
                );
              })}
              </div>
            </div>
          )}

          {tab === "payments" && (
            <div data-testid="tab-content-payments" className="space-y-4">
              <BulkBar
                testid="payments-bulk"
                total={overview.payments.length}
                selected={selectedPayments.size}
                onSelectAll={toggleAllInSet(setSelectedPayments, overview.payments.map((p) => p.session_id || p.id).filter(Boolean))}
                onClear={() => setSelectedPayments(new Set())}
                onRemove={() => {
                  // Default the refund toggle ON if any selected row is paid.
                  const paidSelected = overview.payments.some(
                    (p) => selectedPayments.has(p.session_id || p.id) &&
                           (p.payment_status || p.status) === "paid",
                  );
                  setBulkConfirm({ kind: "payments", ids: [...selectedPayments], refund: paidSelected });
                }}
                label="payments"
              />
              <AdminPaymentsFolders
                payments={overview.payments}
                selectedPayments={selectedPayments}
                togglePayment={togglePayment}
                deletePayment={deletePayment}
                deleteFolder={deleteFolder}
                fmtDate={fmtDate}
              />
            </div>
          )}

          {tab === "expenses" && (
            <div data-testid="tab-content-expenses" className="space-y-4">
              <div className="flex items-center justify-between gap-3 rounded-2xl border border-[#1e3d3a]/12 bg-white p-5">
                <div>
                  <span className="text-[10px] uppercase tracking-[0.28em] text-[#1e3d3a]/60">Expense AI</span>
                  <p className="mt-1 text-sm text-[#1e3d3a]/70 max-w-xl">
                    Run a full expense analysis for a client — CSV import, category budgets, AI risk flags,
                    Excel and PDF exports, scheduled owner summaries.
                  </p>
                </div>
                <Link
                  to="/expenses"
                  target="_blank"
                  rel="noopener"
                  data-testid="open-expense-workspace"
                  className="shrink-0 inline-flex items-center gap-2 rounded-full bg-[#1e3d3a] text-white px-5 py-2.5 text-sm font-semibold hover:bg-[#c9a961] hover:text-[#1e3d3a] transition-colors duration-300"
                >
                  Open workspace <ArrowUpRight className="h-4 w-4" />
                </Link>
              </div>

              <div className="rounded-2xl border border-[#1e3d3a]/12 bg-white overflow-hidden">
              {!expensesLoading && expenses && expenses.length > 0 && (
                <div className="flex items-center gap-3 px-5 py-3 border-b border-[#1e3d3a]/8 bg-[#eae7de]/60">
                  <Search className="h-4 w-4 text-[#1e3d3a]/50" strokeWidth={1.6} />
                  <input
                    data-testid="expense-search"
                    type="text"
                    value={expenseQuery}
                    onChange={(e) => setExpenseQuery(e.target.value)}
                    placeholder="Search by business name or client ID…"
                    className="flex-1 bg-transparent text-sm text-[#1e3d3a] placeholder:text-[#1e3d3a]/40 focus:outline-none"
                  />
                  {expenseQuery && (
                    <button
                      type="button"
                      data-testid="expense-search-clear"
                      onClick={() => setExpenseQuery("")}
                      className="text-[#1e3d3a]/45 hover:text-[#1e3d3a] transition-colors duration-300"
                      title="Clear"
                    >
                      <X className="h-4 w-4" strokeWidth={1.6} />
                    </button>
                  )}
                  <span className="text-[10px] uppercase tracking-[0.22em] text-[#1e3d3a]/50 tabular-nums">
                    {filteredExpenses.length} / {expenses.length}
                  </span>
                </div>
              )}
              {expensesLoading && (
                <div className="py-14 flex items-center justify-center gap-3 text-[#1e3d3a]/55">
                  <Loader2 className="h-4 w-4 animate-spin" /> Loading client analyses…
                </div>
              )}
              {!expensesLoading && expenses && expenses.length === 0 && (
                <div className="py-14 text-center text-[#1e3d3a]/45 text-sm">
                  No expense analyses yet. Clients get added via the Expense AI workspace.
                </div>
              )}
              {!expensesLoading && expenses && expenses.length > 0 && (
                <div className="px-4 py-2 border-b border-[#1e3d3a]/8">
                  <BulkBar
                    testid="expenses-bulk"
                    total={filteredExpenses.length}
                    selected={selectedExpenses.size}
                    onSelectAll={toggleAllInSet(setSelectedExpenses, filteredExpenses.map((a) => a.id).filter(Boolean))}
                    onClear={() => setSelectedExpenses(new Set())}
                    onRemove={() => setBulkConfirm({ kind: "expenses", ids: [...selectedExpenses] })}
                    label="analyses"
                    inline
                  />
                </div>
              )}
              {!expensesLoading && expenses && expenses.length > 0 && (
                <table className="w-full text-sm">
                  <thead className="bg-[#eae7de] text-[10px] uppercase tracking-[0.25em] text-[#1e3d3a]/60">
                    <tr>
                      <th className="text-left px-4 py-3 w-8">
                        <input
                          type="checkbox"
                          data-testid="expenses-select-all"
                          checked={filteredExpenses.length > 0 && filteredExpenses.every((a) => selectedExpenses.has(a.id))}
                          onChange={toggleAllInSet(setSelectedExpenses, filteredExpenses.map((a) => a.id).filter(Boolean))}
                          className="h-3.5 w-3.5 accent-[#a3823f] cursor-pointer"
                        />
                      </th>
                      <th className="text-left px-5 py-3">Analysed</th>
                      <th className="text-left px-5 py-3">Business</th>
                      <th className="text-left px-5 py-3">Client ID</th>
                      <th className="text-right px-5 py-3">Monthly spend</th>
                      <th className="text-right px-5 py-3">Savings potential</th>
                      <th className="text-right px-5 py-3 w-40">Worksheet</th>
                      <th className="text-right px-5 py-3 w-32">Vault</th>
                      <th className="text-right px-5 py-3 w-16"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredExpenses.length === 0 && (
                      <tr><td colSpan="9" className="py-10 text-center text-[#1e3d3a]/45 text-sm">No matches for “{expenseQuery}”.</td></tr>
                    )}
                    {filteredExpenses.map((a) => {
                      const spend = a.total_monthly ?? a.computed_total_monthly ?? 0;
                      const savings = a.total_potential_monthly_savings ?? 0;
                      const busy = downloadingExpenseId === a.id;
                      const vaultOpen = expandedVault === a.client_id;
                      const bucket = a.client_id ? vaultData[a.client_id] : null;
                      const isSel = a.id && selectedExpenses.has(a.id);
                      const rows = [
                        <tr key={a.id} className={`border-t border-[#1e3d3a]/8 group transition-colors duration-200 ${isSel ? "bg-[#c9a961]/8" : ""}`}>
                          <td className="px-4 py-3">
                            {a.id && (
                              <input
                                type="checkbox"
                                data-testid={`expense-select-${a.id}`}
                                checked={isSel}
                                onChange={() => toggleExpense(a.id)}
                                className="h-3.5 w-3.5 accent-[#a3823f] cursor-pointer"
                              />
                            )}
                          </td>
                          <td className="px-5 py-3 text-[#1e3d3a]/70 whitespace-nowrap">{fmtDate(a.created_at)}</td>
                          <td className="px-5 py-3 font-medium text-[#1e3d3a]">{a.business_name || "—"}</td>
                          <td className="px-5 py-3 text-[#1e3d3a]/60 font-mono text-[11px]">{a.client_id || "—"}</td>
                          <td className="px-5 py-3 text-right tabular-nums text-[#1e3d3a]">${Number(spend).toLocaleString()}</td>
                          <td className="px-5 py-3 text-right tabular-nums text-[#4b7a3a]">${Number(savings).toLocaleString()}</td>
                          <td className="px-5 py-3 text-right">
                            <button
                              type="button"
                              data-testid={`download-expense-${a.id}`}
                              onClick={() => downloadExpensePDF(a)}
                              disabled={busy}
                              className="inline-flex items-center gap-1.5 rounded-full border border-[#1e3d3a]/15 bg-white px-3 py-1.5 text-[11px] uppercase tracking-[0.2em] text-[#1e3d3a] hover:border-[#c9a961] hover:text-[#a3823f] transition-colors duration-300 disabled:opacity-60"
                            >
                              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileDown className="h-3.5 w-3.5" />}
                              {busy ? "Building…" : "PDF"}
                            </button>
                          </td>
                          <td className="px-5 py-3 text-right">
                            <button
                              type="button"
                              data-testid={`vault-toggle-${a.id}`}
                              onClick={() => toggleVault(a.client_id)}
                              disabled={!a.client_id}
                              className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-[11px] uppercase tracking-[0.2em] transition-colors duration-300 disabled:opacity-40 ${vaultOpen ? "border-[#c9a961] bg-[#c9a961]/10 text-[#a3823f]" : "border-[#1e3d3a]/15 bg-white text-[#1e3d3a] hover:border-[#c9a961] hover:text-[#a3823f]"}`}
                            >
                              <Paperclip className="h-3.5 w-3.5" />
                              Files
                              <ChevronDown className={`h-3 w-3 transition-transform duration-300 ${vaultOpen ? "rotate-180" : ""}`} />
                            </button>
                          </td>
                          <td className="px-5 py-3 text-right">
                            <button
                              type="button"
                              data-testid={`expense-delete-${a.id}`}
                              onClick={() => deleteExpenseAnalysis(a)}
                              title="Remove this analysis"
                              className="text-[#1e3d3a]/30 hover:text-[#b23a48] transition-colors duration-300 opacity-0 group-hover:opacity-100 focus:opacity-100"
                            >
                              <Trash2 className="h-3.5 w-3.5" strokeWidth={1.8} />
                            </button>
                          </td>
                        </tr>,
                      ];
                      if (vaultOpen) {
                        rows.push(
                          <tr key={`${a.id}-vault`} data-testid={`vault-drawer-${a.id}`} className="border-t border-[#c9a961]/25 bg-[#eae7de]/40">
                            <td colSpan="9" className="px-5 py-4">
                              {/* ---- Notes ---- */}
                              {(() => {
                                const nb = notesData[a.client_id];
                                const draft = noteDrafts[a.client_id] || "";
                                return (
                                  <div className="mb-4 rounded-lg border border-[#1e3d3a]/12 bg-white p-3">
                                    <div className="flex items-center justify-between mb-2">
                                      <div className="text-[10px] uppercase tracking-[0.22em] text-[#a3823f] flex items-center gap-1.5">
                                        <StickyNote className="h-3.5 w-3.5" /> Notes
                                      </div>
                                      <span className="text-[10px] uppercase tracking-[0.2em] text-[#1e3d3a]/45 tabular-nums">
                                        {nb?.notes?.length || 0}
                                      </span>
                                    </div>
                                    <div className="flex gap-2 mb-3">
                                      <textarea
                                        data-testid={`admin-note-input-${a.client_id}`}
                                        rows={2}
                                        value={draft}
                                        onChange={(e) => setNoteDrafts((d) => ({ ...d, [a.client_id]: e.target.value }))}
                                        placeholder={`Add a note about ${a.business_name || a.client_id}…`}
                                        className="flex-1 text-xs rounded border border-[#1e3d3a]/15 bg-[#eae7de]/40 px-2 py-1.5 focus:border-[#c9a961] focus:outline-none resize-y"
                                        onKeyDown={(e) => {
                                          if ((e.metaKey || e.ctrlKey) && e.key === "Enter") addClientNote(a.client_id, a.business_name);
                                        }}
                                      />
                                      <button
                                        type="button"
                                        data-testid={`admin-note-save-${a.client_id}`}
                                        onClick={() => addClientNote(a.client_id, a.business_name)}
                                        disabled={savingNoteCid === a.client_id || !draft.trim()}
                                        className="self-start inline-flex items-center gap-1.5 rounded-full bg-[#1e3d3a] text-white px-3 py-1.5 text-[11px] uppercase tracking-[0.2em] hover:bg-[#c9a961] hover:text-[#1e3d3a] disabled:opacity-50 transition-colors duration-300"
                                        title="Save note (⌘/Ctrl + Enter)"
                                      >
                                        {savingNoteCid === a.client_id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
                                        Add
                                      </button>
                                    </div>
                                    {nb?.loading && (
                                      <div className="flex items-center gap-2 text-[11px] text-[#1e3d3a]/55">
                                        <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading notes…
                                      </div>
                                    )}
                                    {!nb?.loading && (nb?.notes?.length || 0) === 0 && (
                                      <div className="text-[11px] text-[#1e3d3a]/45 italic">No notes yet.</div>
                                    )}
                                    {!nb?.loading && nb?.notes?.length > 0 && (
                                      <ul className="space-y-2 max-h-64 overflow-y-auto pr-1">
                                        {nb.notes.map((note) => {
                                          const editing = editingNoteId === note.id;
                                          return (
                                            <li key={note.id} data-testid={`admin-note-${note.id}`} className={`rounded-lg border p-2.5 ${note.pinned ? "border-[#c9a961]/60 bg-[#c9a961]/8" : "border-[#1e3d3a]/10 bg-[#eae7de]/30"}`}>
                                              <div className="flex items-start justify-between gap-2 mb-1">
                                                <div className="text-[10px] text-[#1e3d3a]/55 uppercase tracking-[0.18em] flex items-center gap-1">
                                                  {note.pinned && <Pin className="h-3 w-3 text-[#a3823f]" strokeWidth={2} />}
                                                  {note.author || "Admin"} · {fmtDate(note.created_at)}
                                                  {note.updated_at && note.updated_at !== note.created_at && (
                                                    <span className="ml-1 italic normal-case tracking-normal">(edited {fmtDate(note.updated_at)})</span>
                                                  )}
                                                </div>
                                                <div className="flex items-center gap-1 shrink-0">
                                                  {!editing && (
                                                    <>
                                                      <button
                                                        type="button"
                                                        data-testid={`admin-note-pin-${note.id}`}
                                                        onClick={() => togglePinNote(a.client_id, note.id)}
                                                        className={`transition-colors duration-300 ${note.pinned ? "text-[#a3823f] hover:text-[#1e3d3a]" : "text-[#1e3d3a]/40 hover:text-[#a3823f]"}`}
                                                        title={note.pinned ? "Unpin" : "Pin to top"}
                                                      >
                                                        {note.pinned ? <PinOff className="h-3 w-3" strokeWidth={1.8} /> : <Pin className="h-3 w-3" strokeWidth={1.8} />}
                                                      </button>
                                                      <button
                                                        type="button"
                                                        data-testid={`admin-note-edit-${note.id}`}
                                                        onClick={() => startEditNote(note)}
                                                        className="text-[#1e3d3a]/40 hover:text-[#a3823f] transition-colors duration-300"
                                                        title="Edit"
                                                      >
                                                        <Pencil className="h-3 w-3" strokeWidth={1.8} />
                                                      </button>
                                                    </>
                                                  )}
                                                  <button
                                                    type="button"
                                                    data-testid={`admin-note-delete-${note.id}`}
                                                    onClick={() => deleteClientNote(a.client_id, note.id)}
                                                    className="text-[#1e3d3a]/40 hover:text-[#b23a48] transition-colors duration-300"
                                                    title="Delete"
                                                  >
                                                    <Trash2 className="h-3 w-3" strokeWidth={1.8} />
                                                  </button>
                                                </div>
                                              </div>
                                              {editing ? (
                                                <div className="space-y-2">
                                                  <textarea
                                                    data-testid={`admin-note-edit-input-${note.id}`}
                                                    rows={3}
                                                    value={editingNoteText}
                                                    onChange={(e) => setEditingNoteText(e.target.value)}
                                                    className="w-full text-xs rounded border border-[#c9a961]/50 bg-white px-2 py-1.5 focus:border-[#c9a961] focus:outline-none resize-y"
                                                  />
                                                  <div className="flex items-center gap-2">
                                                    <button
                                                      type="button"
                                                      data-testid={`admin-note-save-edit-${note.id}`}
                                                      onClick={() => saveEditNote(a.client_id, note.id)}
                                                      className="inline-flex items-center gap-1.5 rounded-full bg-[#1e3d3a] text-white px-3 py-1 text-[10px] uppercase tracking-[0.2em] hover:bg-[#c9a961] hover:text-[#1e3d3a] transition-colors duration-300"
                                                    >
                                                      <Save className="h-3 w-3" /> Save
                                                    </button>
                                                    <button
                                                      type="button"
                                                      onClick={() => { setEditingNoteId(null); setEditingNoteText(""); }}
                                                      className="text-[10px] uppercase tracking-[0.2em] text-[#1e3d3a]/55 hover:text-[#b23a48] px-2"
                                                    >
                                                      Cancel
                                                    </button>
                                                  </div>
                                                </div>
                                              ) : (
                                                <div className="text-xs text-[#1e3d3a] whitespace-pre-wrap leading-relaxed">{note.text}</div>
                                              )}
                                            </li>
                                          );
                                        })}
                                      </ul>
                                    )}
                                  </div>
                                );
                              })()}

                              <div className="text-[10px] uppercase tracking-[0.22em] text-[#a3823f] flex items-center gap-1.5 mb-2">
                                <Paperclip className="h-3.5 w-3.5" /> Files
                              </div>
                              {bucket?.loading && (
                                <div className="flex items-center gap-2 text-xs text-[#1e3d3a]/55">
                                  <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading vault for <span className="font-mono">{a.client_id}</span>…
                                </div>
                              )}
                              {!bucket?.loading && bucket?.files?.length === 0 && (
                                <div className="text-xs text-[#1e3d3a]/55 mb-3">
                                  No files uploaded for this client yet. Files added via the workspace dropzone appear here.
                                </div>
                              )}
                              <div className="mb-3 rounded-lg border border-dashed border-[#c9a961]/50 bg-[#c9a961]/5 px-3 py-2">
                                <div className="flex items-center justify-between gap-2">
                                  <div className="text-[10px] uppercase tracking-[0.22em] text-[#a3823f]">Add document</div>
                                  <label
                                    data-testid={`admin-vault-upload-${a.client_id}`}
                                    className="inline-flex items-center gap-1.5 rounded-full border border-[#1e3d3a]/15 bg-white px-3 py-1.5 text-[11px] uppercase tracking-[0.2em] text-[#1e3d3a] hover:border-[#c9a961] hover:text-[#a3823f] cursor-pointer transition-colors duration-300"
                                  >
                                    {uploadingCid === a.client_id
                                      ? <><Loader2 className="h-3.5 w-3.5 animate-spin" /> {uploadProgress?.cid === a.client_id ? `${uploadProgress.pct}%` : "Uploading…"}</>
                                      : <><Paperclip className="h-3.5 w-3.5" /> Choose file</>}
                                    <input
                                      type="file"
                                      className="hidden"
                                      disabled={uploadingCid === a.client_id}
                                      onChange={(e) => {
                                        if (e.target.files?.[0]) uploadVaultFile(a.client_id, a.business_name, e.target.files[0]);
                                        e.target.value = "";
                                      }}
                                    />
                                  </label>
                                </div>
                                {uploadingCid === a.client_id && uploadProgress?.cid === a.client_id && (
                                  <div className="mt-2 space-y-1" data-testid={`admin-vault-progress-${a.client_id}`}>
                                    <div className="flex items-center justify-between gap-2 text-[10px] text-[#1e3d3a]/60">
                                      <span className="truncate">{uploadProgress.name}</span>
                                      <span className="tabular-nums shrink-0">{fmtBytes(uploadProgress.loaded)} / {fmtBytes(uploadProgress.total)}</span>
                                    </div>
                                    <div className="h-1.5 w-full rounded-full bg-[#1e3d3a]/10 overflow-hidden">
                                      <div
                                        className="h-full rounded-full bg-[#c9a961] transition-[width] duration-200 ease-out"
                                        style={{ width: `${uploadProgress.pct}%` }}
                                      />
                                    </div>
                                  </div>
                                )}
                              </div>
                              {!bucket?.loading && bucket?.files?.length > 0 && (() => {
                                const q = (vaultQueries[a.client_id] || "").trim().toLowerCase();
                                const visible = q
                                  ? bucket.files.filter((f) => (f.filename || "").toLowerCase().includes(q))
                                  : bucket.files;
                                return (
                                  <>
                                    <div className="mb-3 flex items-center gap-2 rounded-lg border border-[#1e3d3a]/12 bg-white px-3 py-1.5">
                                      <Search className="h-3.5 w-3.5 text-[#1e3d3a]/45" strokeWidth={1.6} />
                                      <input
                                        type="text"
                                        data-testid={`admin-vault-search-${a.client_id}`}
                                        value={vaultQueries[a.client_id] || ""}
                                        onChange={(e) => setVaultQueries((v) => ({ ...v, [a.client_id]: e.target.value }))}
                                        placeholder="Search files by name…"
                                        className="flex-1 bg-transparent text-xs text-[#1e3d3a] placeholder:text-[#1e3d3a]/40 focus:outline-none"
                                      />
                                      {q && (
                                        <button
                                          type="button"
                                          data-testid={`admin-vault-search-clear-${a.client_id}`}
                                          onClick={() => setVaultQueries((v) => ({ ...v, [a.client_id]: "" }))}
                                          className="text-[#1e3d3a]/40 hover:text-[#1e3d3a] transition-colors duration-300"
                                          title="Clear"
                                        >
                                          <X className="h-3.5 w-3.5" strokeWidth={1.6} />
                                        </button>
                                      )}
                                      <span className="text-[10px] uppercase tracking-[0.2em] text-[#1e3d3a]/50 tabular-nums">
                                        {visible.length}/{bucket.files.length}
                                      </span>
                                    </div>
                                    {visible.length === 0 ? (
                                      <div className="text-xs text-[#1e3d3a]/45 py-2">No files match &ldquo;{q}&rdquo;.</div>
                                    ) : (
                                      <ul className="space-y-1.5">
                                        {visible.map((f) => (
                                    <li key={f.id} data-testid={`admin-vault-file-${f.id}`} className="flex items-center justify-between gap-3 rounded-lg bg-white border border-[#1e3d3a]/12 px-3 py-2">
                                      <button
                                        type="button"
                                        data-testid={`admin-vault-download-${f.id}`}
                                        onClick={() => downloadVaultFile(f)}
                                        className="flex items-center gap-2 min-w-0 text-left text-xs text-[#1e3d3a] hover:text-[#a3823f] transition-colors duration-300"
                                      >
                                        <FileText className="h-3.5 w-3.5 shrink-0" />
                                        <span className="truncate">{f.filename}</span>
                                        <span className="text-[10px] text-[#1e3d3a]/45 whitespace-nowrap">· {fmtBytes(f.size)}</span>
                                      </button>
                                      <div className="flex items-center gap-3 shrink-0 text-[10px] text-[#1e3d3a]/45">
                                        <span className="whitespace-nowrap">{fmtDate(f.uploaded_at)}</span>
                                        {isViewable(f) && (
                                          <button
                                            type="button"
                                            data-testid={`admin-vault-view-${f.id}`}
                                            onClick={() => previewVaultFile(f)}
                                            className="inline-flex items-center gap-1 text-[#1e3d3a]/55 hover:text-[#a3823f] transition-colors duration-300"
                                            title="View in browser"
                                          >
                                            <Eye className="h-3.5 w-3.5" strokeWidth={1.6} /> View
                                          </button>
                                        )}
                                        <button
                                          type="button"
                                          data-testid={`admin-vault-delete-${f.id}`}
                                          onClick={() => deleteVaultFile(f)}
                                          className="text-[#1e3d3a]/40 hover:text-[#b23a48] transition-colors duration-300"
                                          title="Remove"
                                        >
                                          <Trash2 className="h-3.5 w-3.5" strokeWidth={1.6} />
                                        </button>
                                      </div>
                                    </li>
                                        ))}
                                      </ul>
                                    )}
                                  </>
                                );
                              })()}
                            </td>
                          </tr>
                        );
                      }
                      return rows;
                    })}
                  </tbody>
                </table>
              )}
              </div>
            </div>
          )}

          {tab === "progress" && (
            <ClientProgress secret={secret} />
          )}
          {tab === "ai-ops" && (
            <AiOps secret={secret} />
          )}
          {tab === "vault" && (
            <div data-testid="tab-content-vault" className="space-y-4">
              <AnniversaryAlerts />
              <ClientVault onSendPlan={me?.role === "founder" ? setProposeFor : null} planSent={planSent} onLaunchAssessment={launchAssessment} />
            </div>
          )}
          {proposeFor && <ProposePlanModal initial={proposeFor} onClose={() => setProposeFor(null)} onSent={refreshPlanSent} />}
          {tab === "health" && (
            <div data-testid="tab-content-health">
              <BusinessHealthBoard />
            </div>
          )}
          {tab === "transfers" && (
            <AdminTransfers />
          )}

          {tab === "reputation" && (
            <div data-testid="tab-content-reputation">
              <AdminReputationRepair />
            </div>
          )}

          {tab === "prospector" && (
            <ProspectorTab seed={prospectorSeed} />
          )}

          {tab === "staff" && (
            <div data-testid="tab-content-staff-wrapper">
              <StaffAdmin />
            </div>
          )}

          {tab === "access" && me?.role === "founder" && (
            <AdminAccess tabsMeta={GRANTABLE_TABS.map((t) => ({ id: t.id, label: t.label, sensitive: !!t.sensitive }))} />
          )}

          {tab === "subscriptions" && (
            <div data-testid="tab-content-subscriptions-wrapper">
              <SubscriptionsCard />
            </div>
          )}

          {tab === "bank" && (
            <div data-testid="tab-content-bank-wrapper" className="space-y-5">
              <PayrollCard />
              <FoundersBank />
            </div>
          )}

          {tab === "downloads" && (
            <div className="space-y-5">
              <OverviewDownloads />
              <DownloadApprovals />
            </div>
          )}

          {tab === "case-studies" && (
            <CaseStudiesAdmin />
          )}

          {tab === "referrals" && (
            <ReferralPartners />
          )}

          {tab === "recommendations" && (
            <RecommendationsBoard />
          )}

          {tab === "support" && (
            <SupportBoard />
          )}

          {tab === "workspace" && (
            <AdminWorkspace />
          )}

          {tab === "handbook" && (
            <HandbookBoard onOpenTab={setTab} />
          )}

          {/* Delete-confirm modals — mounted at the dashboard level so they work
              on EVERY tab (Payments, Predictions, Expenses), not just Leads. */}
          {paymentDeleteTarget && (
            <PaymentDeleteModal
              target={paymentDeleteTarget}
              busy={paymentDeleting}
              onChangeRefund={(v) => setPaymentDeleteTarget((t) => t ? { ...t, refund: v } : t)}
              onConfirm={confirmDeletePayment}
              onClose={() => setPaymentDeleteTarget(null)}
            />
          )}
          {bulkConfirm && (
            <BulkConfirmModal
              target={bulkConfirm}
              busy={bulkDeleting}
              onChangeRefund={(v) => setBulkConfirm((t) => t ? { ...t, refund: v } : t)}
              onConfirm={confirmBulkDelete}
              onClose={() => setBulkConfirm(null)}
            />
          )}
          </div>
        </div>
      </main>
    </div>
  );
}

function AddLeadModal({ draft, setDraft, saving, onClose, onSave }) {
  const inputCls = "w-full rounded-lg border border-black/15 bg-white px-3 py-2 text-sm text-[#1e3d3a] placeholder:text-black/30 focus:border-[#c9a961] focus:outline-none focus:ring-2 focus:ring-[#c9a961]/20 transition-colors duration-300";
  const labelCls = "text-[10px] uppercase tracking-[0.22em] text-black/45";
  const set = (k) => (e) => setDraft({ ...draft, [k]: e.target.value });
  return (
    <div
      data-testid="add-lead-modal"
      className="fixed inset-0 z-50 bg-black/45 backdrop-blur-sm flex items-center justify-center px-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="w-full max-w-lg rounded-2xl bg-[#ded9cb] border border-[#c9a961]/40 p-6 shadow-2xl">
        <div className="flex items-center justify-between mb-1">
          <p className="text-[10px] uppercase tracking-[0.24em] text-[#a3823f]">New lead</p>
          <button type="button" onClick={onClose} data-testid="add-lead-close" className="text-black/40 hover:text-[#1e3d3a] transition-colors duration-300">
            <X className="h-4 w-4" />
          </button>
        </div>
        <h3 className="mt-1 font-head text-2xl text-[#1e3d3a]">Add a lead</h3>
        <p className="mt-1 text-xs text-black/55 leading-relaxed">
          Log a lead from a phone call, referral, or in-person conversation. Only name &amp; email are required.
        </p>

        <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className={labelCls}>Name *</label>
            <input data-testid="add-lead-name" className={"mt-1 " + inputCls} value={draft.name} onChange={set("name")} placeholder="Jamie Rivera" />
          </div>
          <div>
            <label className={labelCls}>Email *</label>
            <input data-testid="add-lead-email" type="email" className={"mt-1 " + inputCls} value={draft.email} onChange={set("email")} placeholder="jamie@ridgeandrowan.com" />
          </div>
          <div>
            <label className={labelCls}>Company</label>
            <input data-testid="add-lead-company" className={"mt-1 " + inputCls} value={draft.company} onChange={set("company")} placeholder="Ridge &amp; Rowan Cafe" />
          </div>
          <div>
            <label className={labelCls}>Phone</label>
            <input data-testid="add-lead-phone" type="tel" className={"mt-1 " + inputCls} value={draft.phone} onChange={set("phone")} placeholder="(555) 555-5555" />
          </div>
          <div className="sm:col-span-2">
            <label className={labelCls}>Source</label>
            <input data-testid="add-lead-source" className={"mt-1 " + inputCls} value={draft.source} onChange={set("source")} placeholder="Referral · phone call · LinkedIn · Kalib's network" />
          </div>
          <div className="sm:col-span-2">
            <label className={labelCls}>Notes</label>
            <textarea data-testid="add-lead-notes" rows={3} className={"mt-1 " + inputCls + " resize-y"} value={draft.notes} onChange={set("notes")} placeholder="What's their pain point? What did we talk about?" />
          </div>
        </div>

        <div className="mt-6 flex items-center gap-3">
          <button
            type="button"
            data-testid="add-lead-save"
            onClick={onSave}
            disabled={saving}
            className="flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-[#1e3d3a] text-white px-4 py-2.5 text-[11px] uppercase tracking-[0.22em] hover:bg-[#c9a961] hover:text-[#1e3d3a] disabled:opacity-60 transition-colors duration-300"
          >
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
            Save lead
          </button>
          <button
            type="button"
            onClick={onClose}
            className="text-[11px] uppercase tracking-[0.22em] text-black/45 hover:text-[#1e3d3a] transition-colors duration-300 px-3"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}


function PaymentDeleteModal({ target, busy, onChangeRefund, onConfirm, onClose }) {
  if (!target) return null;
  const { p, refund } = target;
  const isPaid = (p.payment_status || p.status) === "paid";
  const label = p.email || p.session_id || "this payment";
  const amount = (p.amount || 0).toLocaleString(undefined, { style: "currency", currency: (p.currency || "usd").toUpperCase() });
  return (
    <div
      data-testid="payment-delete-modal"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
      onClick={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}
    >
      <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
        <p className="text-[10px] uppercase tracking-[0.28em] text-[#a3823f]">Remove payment row</p>
        <h3 className="mt-1 font-head text-xl text-[#1e3d3a]">Remove {amount}?</h3>
        <p className="mt-2 text-sm text-[#1e3d3a]/70">
          Removing the row from <span className="font-semibold">{label}</span> only hides it from your dashboard.
          {isPaid && " Stripe still holds the money unless you also refund below."}
        </p>

        {isPaid && (
          <label className="mt-4 flex items-start gap-3 rounded-xl border border-[#c9a961]/40 bg-[#c9a961]/8 p-3 cursor-pointer">
            <input
              type="checkbox"
              data-testid="payment-delete-refund-toggle"
              checked={refund}
              onChange={(e) => onChangeRefund(e.target.checked)}
              disabled={busy}
              className="mt-0.5 h-4 w-4 accent-[#a3823f]"
            />
            <span className="text-sm text-[#1e3d3a]">
              <span className="font-medium">Also issue a full Stripe refund</span>
              <span className="block text-[11px] text-[#1e3d3a]/60 mt-0.5">
                The customer sees the refund on their card in 5-10 business days. Cannot be undone.
              </span>
            </span>
          </label>
        )}

        <div className="mt-6 flex items-center gap-3">
          <button
            type="button"
            data-testid="payment-delete-confirm"
            onClick={onConfirm}
            disabled={busy}
            className={`flex-1 inline-flex items-center justify-center gap-2 rounded-full px-4 py-2.5 text-[11px] uppercase tracking-[0.22em] disabled:opacity-60 transition-colors duration-300 ${
              refund
                ? "bg-[#b23a48] text-white hover:bg-[#8f2a37]"
                : "bg-[#1e3d3a] text-white hover:bg-[#c9a961] hover:text-[#1e3d3a]"
            }`}
          >
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
            {refund ? "Refund & remove" : "Remove row"}
          </button>
          <button
            type="button"
            data-testid="payment-delete-cancel"
            onClick={onClose}
            disabled={busy}
            className="text-[11px] uppercase tracking-[0.22em] text-black/45 hover:text-[#1e3d3a] transition-colors duration-300 px-3 disabled:opacity-60"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

function BulkBar({ testid, total, selected, onSelectAll, onClear, onRemove, label = "rows", inline = false }) {
  if (!total) return null;
  const allSelected = selected > 0 && selected >= total;
  return (
    <div
      data-testid={testid}
      className={
        (inline ? "" : "rounded-2xl border border-[#1e3d3a]/12 bg-white px-4 py-2 ")
        + "flex items-center gap-3 flex-wrap"
      }
    >
      <label className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-[#1e3d3a]/60 cursor-pointer">
        <input
          type="checkbox"
          data-testid={`${testid}-select-all`}
          checked={allSelected}
          onChange={onSelectAll}
          className="h-3.5 w-3.5 accent-[#a3823f] cursor-pointer"
        />
        {allSelected ? "Deselect all" : `Select all ${total}`}
      </label>
      {selected > 0 && (
        <>
          <span className="text-[10px] uppercase tracking-[0.22em] text-[#a3823f] tabular-nums">
            {selected} selected
          </span>
          <button
            type="button"
            data-testid={`${testid}-remove`}
            onClick={onRemove}
            className="ml-auto inline-flex items-center gap-1.5 rounded-full bg-[#b23a48] text-white px-3 py-1.5 text-[10px] uppercase tracking-[0.22em] hover:bg-[#8f2a37] transition-colors duration-300"
          >
            <Trash2 className="h-3 w-3" strokeWidth={2} />
            Remove {selected} {label}
          </button>
          <button
            type="button"
            data-testid={`${testid}-clear`}
            onClick={onClear}
            className="text-[10px] uppercase tracking-[0.22em] text-[#1e3d3a]/50 hover:text-[#1e3d3a] transition-colors duration-300"
          >
            Clear
          </button>
        </>
      )}
    </div>
  );
}


function BulkConfirmModal({ target, busy, onChangeRefund, onConfirm, onClose }) {
  if (!target) return null;
  const { kind, ids, refund } = target;
  const KIND_META = {
    predictions: { label: "forecast", labelPlural: "forecasts", tone: "Removes the AI forecast cards permanently." },
    payments: { label: "payment row", labelPlural: "payment rows", tone: "Removes the ledger rows from your dashboard. Stripe still holds the money unless you also refund below." },
    expenses: { label: "analysis", labelPlural: "analyses", tone: "Deletes the Expense AI worksheets permanently." },
  };
  const meta = KIND_META[kind] || { label: "row", labelPlural: "rows", tone: "" };
  const noun = ids.length === 1 ? meta.label : meta.labelPlural;
  return (
    <div
      data-testid="bulk-confirm-modal"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
      onClick={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}
    >
      <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
        <p className="text-[10px] uppercase tracking-[0.28em] text-[#a3823f]">Bulk remove · {kind}</p>
        <h3 className="mt-1 font-head text-xl text-[#1e3d3a]">Remove {ids.length} {noun}?</h3>
        <p className="mt-2 text-sm text-[#1e3d3a]/70">{meta.tone}</p>

        {kind === "payments" && (
          <label className="mt-4 flex items-start gap-3 rounded-xl border border-[#c9a961]/40 bg-[#c9a961]/8 p-3 cursor-pointer">
            <input
              type="checkbox"
              data-testid="bulk-refund-toggle"
              checked={!!refund}
              onChange={(e) => onChangeRefund(e.target.checked)}
              disabled={busy}
              className="mt-0.5 h-4 w-4 accent-[#a3823f]"
            />
            <span className="text-sm text-[#1e3d3a]">
              <span className="font-medium">Also issue full Stripe refunds for every paid row</span>
              <span className="block text-[11px] text-[#1e3d3a]/60 mt-0.5">
                Pending / unpaid rows are skipped. If a Stripe refund fails, that row stays intact and you'll see an error toast.
              </span>
            </span>
          </label>
        )}

        <div className="mt-6 flex items-center gap-3">
          <button
            type="button"
            data-testid="bulk-confirm"
            onClick={onConfirm}
            disabled={busy || !ids.length}
            className={`flex-1 inline-flex items-center justify-center gap-2 rounded-full px-4 py-2.5 text-[11px] uppercase tracking-[0.22em] disabled:opacity-60 transition-colors duration-300 ${
              refund
                ? "bg-[#b23a48] text-white hover:bg-[#8f2a37]"
                : "bg-[#1e3d3a] text-white hover:bg-[#c9a961] hover:text-[#1e3d3a]"
            }`}
          >
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
            {refund ? `Refund & remove ${ids.length}` : `Remove ${ids.length} ${noun}`}
          </button>
          <button
            type="button"
            data-testid="bulk-cancel"
            onClick={onClose}
            disabled={busy}
            className="text-[11px] uppercase tracking-[0.22em] text-black/45 hover:text-[#1e3d3a] transition-colors duration-300 px-3 disabled:opacity-60"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

